/**
 * Названия записей SRD 5.2 по-русски.
 *
 * Переведены **термины**: классы, подклассы, виды, предыстории,
 * навыки, языки, названия черт, названия заклинаний, инструменты
 * и предметы стартовых наборов. Описания черт, особенностей
 * и заклинаний остаются английскими — это отдельная работа, и она
 * записана открытым вопросом (`docs/open-questions.md`, раздел 7),
 * а не сделана здесь наполовину.
 *
 * Предметы переведены не все: в SRD их четыре с половиной сотни,
 * а на экраны выходят инструменты (визард спрашивает про владение
 * ими) и содержимое стартовых наборов. Список нужного считается
 * из самого контента — им же и проверяется (`terms.test.ts`),
 * поэтому «забыли добавить» здесь означает красный тест, а не
 * английское слово посреди русского экрана.
 *
 * Ключ словаря — ключ записи, то есть `srd:<ключ>` без приставки.
 * Связь именно по идентификатору, а не по имени: имя в источнике
 * может измениться, идентификатор — нет.
 *
 * Английские названия лежат рядом с русскими и не выдумываются:
 * тест сверяет каждое с полем `name` самой записи контента
 * (`terms.test.ts`). Из-за этого второй источник имени невозможен —
 * разошлись бы они молча, а так падает тест.
 *
 * Где хранить перевод в итоге — рядом с записью или отдельным файлом
 * на язык — решается вместе с переводом описаний (open-questions,
 * раздел 7). Здесь выбран отдельный файл: он дешевле и полностью
 * обратим, и на то решение не влияет.
 */

export type Term = {
  readonly ru: string
  readonly en: string
}

export const TERMS: Readonly<Record<string, Term>> = {
  // ── Состояния (глоссарий, статьи `[Condition]`) ──
  //
  // Русские эквиваленты взяты из словаря перевода
  // (`content/translations/glossary.json`, группа `conditions`):
  // одно понятие — один эквивалент во всех местах.
  blinded: { ru: 'Ослеплён', en: 'Blinded' },
  charmed: { ru: 'Очарован', en: 'Charmed' },
  deafened: { ru: 'Оглушён', en: 'Deafened' },
  exhaustion: { ru: 'Истощение', en: 'Exhaustion' },
  frightened: { ru: 'Испуган', en: 'Frightened' },
  grappled: { ru: 'Схвачен', en: 'Grappled' },
  incapacitated: { ru: 'Недееспособен', en: 'Incapacitated' },
  invisible: { ru: 'Невидим', en: 'Invisible' },
  paralyzed: { ru: 'Парализован', en: 'Paralyzed' },
  petrified: { ru: 'Окаменел', en: 'Petrified' },
  poisoned: { ru: 'Отравлен', en: 'Poisoned' },
  prone: { ru: 'Лежит ничком', en: 'Prone' },
  restrained: { ru: 'Обездвижен', en: 'Restrained' },
  stunned: { ru: 'Ошеломлён', en: 'Stunned' },
  unconscious: { ru: 'Без сознания', en: 'Unconscious' },

  // ── Классы (с. 28–82) ──
  barbarian: { ru: 'Варвар', en: 'Barbarian' },
  bard: { ru: 'Бард', en: 'Bard' },
  cleric: { ru: 'Жрец', en: 'Cleric' },
  druid: { ru: 'Друид', en: 'Druid' },
  fighter: { ru: 'Воин', en: 'Fighter' },
  monk: { ru: 'Монах', en: 'Monk' },
  paladin: { ru: 'Паладин', en: 'Paladin' },
  ranger: { ru: 'Следопыт', en: 'Ranger' },
  rogue: { ru: 'Плут', en: 'Rogue' },
  sorcerer: { ru: 'Чародей', en: 'Sorcerer' },
  warlock: { ru: 'Колдун', en: 'Warlock' },
  wizard: { ru: 'Волшебник', en: 'Wizard' },

  // ── Подклассы ──
  champion: { ru: 'Чемпион', en: 'Champion' },
  circle_of_the_land: { ru: 'Круг земли', en: 'Circle of the Land' },
  college_of_lore: { ru: 'Коллегия знаний', en: 'College of Lore' },
  draconic_sorcery: { ru: 'Драконья магия', en: 'Draconic Sorcery' },
  evoker: { ru: 'Воплотитель', en: 'Evoker' },
  fiend_patron: { ru: 'Исчадие', en: 'Fiend Patron' },
  hunter: { ru: 'Охотник', en: 'Hunter' },
  life_domain: { ru: 'Домен жизни', en: 'Life Domain' },
  oath_of_devotion: { ru: 'Клятва преданности', en: 'Oath of Devotion' },
  path_of_the_berserker: { ru: 'Путь берсерка', en: 'Path of the Berserker' },
  thief: { ru: 'Вор', en: 'Thief' },
  warrior_of_the_open_hand: {
    ru: 'Воин открытой ладони',
    en: 'Warrior of the Open Hand',
  },

  // ── Виды (с. 84–86) ──
  dragonborn: { ru: 'Драконорождённый', en: 'Dragonborn' },
  dwarf: { ru: 'Дварф', en: 'Dwarf' },
  elf: { ru: 'Эльф', en: 'Elf' },
  gnome: { ru: 'Гном', en: 'Gnome' },
  goliath: { ru: 'Голиаф', en: 'Goliath' },
  halfling: { ru: 'Полурослик', en: 'Halfling' },
  human: { ru: 'Человек', en: 'Human' },
  orc: { ru: 'Орк', en: 'Orc' },
  tiefling: { ru: 'Тифлинг', en: 'Tiefling' },

  // ── Предыстории (с. 83) ──
  acolyte: { ru: 'Послушник', en: 'Acolyte' },
  criminal: { ru: 'Преступник', en: 'Criminal' },
  sage: { ru: 'Мудрец', en: 'Sage' },
  soldier: { ru: 'Солдат', en: 'Soldier' },

  // ── Навыки (с. 15) ──
  acrobatics: { ru: 'Акробатика', en: 'Acrobatics' },
  animal_handling: { ru: 'Уход за животными', en: 'Animal Handling' },
  arcana: { ru: 'Магия', en: 'Arcana' },
  athletics: { ru: 'Атлетика', en: 'Athletics' },
  deception: { ru: 'Обман', en: 'Deception' },
  history: { ru: 'История', en: 'History' },
  insight: { ru: 'Проницательность', en: 'Insight' },
  intimidation: { ru: 'Запугивание', en: 'Intimidation' },
  investigation: { ru: 'Анализ', en: 'Investigation' },
  medicine: { ru: 'Медицина', en: 'Medicine' },
  nature: { ru: 'Природа', en: 'Nature' },
  perception: { ru: 'Восприятие', en: 'Perception' },
  performance: { ru: 'Выступление', en: 'Performance' },
  persuasion: { ru: 'Убеждение', en: 'Persuasion' },
  religion: { ru: 'Религия', en: 'Religion' },
  sleight_of_hand: { ru: 'Ловкость рук', en: 'Sleight of Hand' },
  stealth: { ru: 'Скрытность', en: 'Stealth' },
  survival: { ru: 'Выживание', en: 'Survival' },

  // ── Языки (с. 25) ──
  abyssal: { ru: 'Бездонный', en: 'Abyssal' },
  celestial: { ru: 'Небесный', en: 'Celestial' },
  common: { ru: 'Общий', en: 'Common' },
  common_sign_language: { ru: 'Общий язык жестов', en: 'Common Sign Language' },
  deep_speech: { ru: 'Глубинная речь', en: 'Deep Speech' },
  draconic: { ru: 'Драконий', en: 'Draconic' },
  druidic: { ru: 'Друидический', en: 'Druidic' },
  dwarvish: { ru: 'Дварфский', en: 'Dwarvish' },
  elvish: { ru: 'Эльфийский', en: 'Elvish' },
  giant: { ru: 'Великаний', en: 'Giant' },
  gnomish: { ru: 'Гномий', en: 'Gnomish' },
  goblin: { ru: 'Гоблинский', en: 'Goblin' },
  halfling_language: { ru: 'Полуросличий', en: 'Halfling' },
  infernal: { ru: 'Инфернальный', en: 'Infernal' },
  orc_language: { ru: 'Орочий', en: 'Orc' },
  primordial: { ru: 'Первичный', en: 'Primordial' },
  sylvan: { ru: 'Сильванский', en: 'Sylvan' },
  thieves_cant: { ru: 'Воровской жаргон', en: 'Thieves’ Cant' },
  undercommon: { ru: 'Подземный', en: 'Undercommon' },

  // ── Черты (с. 87–92) ──
  ability_score_improvement: {
    ru: 'Повышение характеристик',
    en: 'Ability Score Improvement',
  },
  alert: { ru: 'Бдительный', en: 'Alert' },
  archery: { ru: 'Стрельба', en: 'Archery' },
  boon_of_combat_prowess: { ru: 'Дар боевой доблести', en: 'Boon of Combat Prowess' },
  boon_of_dimensional_travel: {
    ru: 'Дар межпространственных путешествий',
    en: 'Boon of Dimensional Travel',
  },
  boon_of_fate: { ru: 'Дар судьбы', en: 'Boon of Fate' },
  boon_of_irresistible_offense: {
    ru: 'Дар неотразимого натиска',
    en: 'Boon of Irresistible Offense',
  },
  boon_of_spell_recall: { ru: 'Дар возврата заклинаний', en: 'Boon of Spell Recall' },
  boon_of_the_night_spirit: { ru: 'Дар ночного духа', en: 'Boon of the Night Spirit' },
  boon_of_truesight: { ru: 'Дар истинного зрения', en: 'Boon of Truesight' },
  defense: { ru: 'Оборона', en: 'Defense' },
  grappler: { ru: 'Борец', en: 'Grappler' },
  great_weapon_fighting: { ru: 'Бой большим оружием', en: 'Great Weapon Fighting' },
  magic_initiate: { ru: 'Посвящённый в магию', en: 'Magic Initiate' },
  savage_attacker: { ru: 'Свирепый боец', en: 'Savage Attacker' },
  skilled: { ru: 'Умелый', en: 'Skilled' },
  two_weapon_fighting: { ru: 'Бой двумя оружиями', en: 'Two-Weapon Fighting' },

  // ── Инструменты ремесленника (с. 96) ──
  alchemists_supplies: { ru: 'Инструменты алхимика', en: 'Alchemist’s Supplies' },
  brewers_supplies: { ru: 'Инструменты пивовара', en: 'Brewer’s Supplies' },
  calligraphers_supplies: {
    ru: 'Инструменты каллиграфа',
    en: 'Calligrapher’s Supplies',
  },
  carpenters_tools: { ru: 'Инструменты плотника', en: 'Carpenter’s Tools' },
  cartographers_tools: { ru: 'Инструменты картографа', en: 'Cartographer’s Tools' },
  cobblers_tools: { ru: 'Инструменты сапожника', en: 'Cobbler’s Tools' },
  cooks_utensils: { ru: 'Инструменты повара', en: 'Cook’s Utensils' },
  glassblowers_tools: { ru: 'Инструменты стеклодува', en: 'Glassblower’s Tools' },
  jewelers_tools: { ru: 'Инструменты ювелира', en: 'Jeweler’s Tools' },
  leatherworkers_tools: { ru: 'Инструменты кожевника', en: 'Leatherworker’s Tools' },
  masons_tools: { ru: 'Инструменты каменщика', en: 'Mason’s Tools' },
  painters_supplies: { ru: 'Инструменты художника', en: 'Painter’s Supplies' },
  potters_tools: { ru: 'Инструменты гончара', en: 'Potter’s Tools' },
  smiths_tools: { ru: 'Инструменты кузнеца', en: 'Smith’s Tools' },
  tinkers_tools: { ru: 'Инструменты жестянщика', en: 'Tinker’s Tools' },
  weavers_tools: { ru: 'Инструменты ткача', en: 'Weaver’s Tools' },
  woodcarvers_tools: { ru: 'Инструменты резчика по дереву', en: 'Woodcarver’s Tools' },

  // ── Прочие инструменты и наборы (с. 96) ──
  disguise_kit: { ru: 'Набор для маскировки', en: 'Disguise Kit' },
  forgery_kit: { ru: 'Набор для подделки документов', en: 'Forgery Kit' },
  herbalism_kit: { ru: 'Набор травника', en: 'Herbalism Kit' },
  navigators_tools: { ru: 'Инструменты навигатора', en: 'Navigator’s Tools' },
  poisoners_kit: { ru: 'Набор отравителя', en: 'Poisoner’s Kit' },
  thieves_tools: { ru: 'Воровские инструменты', en: 'Thieves’ Tools' },

  // ── Игровые наборы (с. 96) ──
  gaming_set: { ru: 'Игровой набор', en: 'Gaming Set' },
  gaming_set_dice: { ru: 'Игровой набор: кости', en: 'Gaming Set, Dice' },
  gaming_set_dragonchess: {
    ru: 'Игровой набор: драконьи шахматы',
    en: 'Gaming Set, Dragonchess',
  },
  gaming_set_playing_cards: {
    ru: 'Игровой набор: игральные карты',
    en: 'Gaming Set, Playing cards',
  },
  gaming_set_three_dragon_ante: {
    ru: 'Игровой набор: ставка трёх драконов',
    en: 'Gaming Set, Three-dragon ante',
  },

  // ── Музыкальные инструменты (с. 96) ──
  musical_instrument: { ru: 'Музыкальный инструмент', en: 'Musical Instrument' },
  musical_instrument_bagpipes: {
    ru: 'Музыкальный инструмент: волынка',
    en: 'Musical Instrument, Bagpipes',
  },
  musical_instrument_drum: {
    ru: 'Музыкальный инструмент: барабан',
    en: 'Musical Instrument, Drum',
  },
  musical_instrument_dulcimer: {
    ru: 'Музыкальный инструмент: цимбалы',
    en: 'Musical Instrument, Dulcimer',
  },
  musical_instrument_flute: {
    ru: 'Музыкальный инструмент: флейта',
    en: 'Musical Instrument, Flute',
  },
  musical_instrument_horn: {
    ru: 'Музыкальный инструмент: рожок',
    en: 'Musical Instrument, Horn',
  },
  musical_instrument_lute: {
    ru: 'Музыкальный инструмент: лютня',
    en: 'Musical Instrument, Lute',
  },
  musical_instrument_lyre: {
    ru: 'Музыкальный инструмент: лира',
    en: 'Musical Instrument, Lyre',
  },
  musical_instrument_pan_flute: {
    ru: 'Музыкальный инструмент: свирель',
    en: 'Musical Instrument, Pan flute',
  },
  musical_instrument_shawm: {
    ru: 'Музыкальный инструмент: шалмей',
    en: 'Musical Instrument, Shawm',
  },
  musical_instrument_viol: {
    ru: 'Музыкальный инструмент: виола',
    en: 'Musical Instrument, Viol',
  },

  // ── Оружие стартовых наборов (с. 92–94) ──
  dagger: { ru: 'Кинжал', en: 'Dagger' },
  flail: { ru: 'Цеп', en: 'Flail' },
  greataxe: { ru: 'Секира', en: 'Greataxe' },
  greatsword: { ru: 'Двуручный меч', en: 'Greatsword' },
  handaxe: { ru: 'Ручной топор', en: 'Handaxe' },
  javelin: { ru: 'Метательное копьё', en: 'Javelin' },
  longbow: { ru: 'Длинный лук', en: 'Longbow' },
  longsword: { ru: 'Длинный меч', en: 'Longsword' },
  mace: { ru: 'Булава', en: 'Mace' },
  quarterstaff: { ru: 'Боевой посох', en: 'Quarterstaff' },
  scimitar: { ru: 'Скимитар', en: 'Scimitar' },
  shortbow: { ru: 'Короткий лук', en: 'Shortbow' },
  shortsword: { ru: 'Короткий меч', en: 'Shortsword' },
  sickle: { ru: 'Серп', en: 'Sickle' },
  spear: { ru: 'Копьё', en: 'Spear' },

  // ── Доспехи, щит и прочее снаряжение стартовых наборов (с. 91–96) ──
  ammunition_arrows: { ru: 'Боеприпасы: стрелы', en: 'Ammunition, Arrows' },
  arcane_focus: { ru: 'Магическая фокусировка', en: 'Arcane Focus' },
  book: { ru: 'Книга', en: 'Book' },
  burglars_pack: { ru: 'Набор взломщика', en: 'Burglar’s Pack' },
  chain_mail: { ru: 'Кольчуга', en: 'Chain Mail' },
  chain_shirt: { ru: 'Кольчужная рубаха', en: 'Chain Shirt' },
  clothes_travelers: { ru: 'Одежда: дорожная', en: 'Clothes, Traveler’s' },
  crowbar: { ru: 'Лом', en: 'Crowbar' },
  druidic_focus: { ru: 'Друидическая фокусировка', en: 'Druidic Focus' },
  dungeoneers_pack: { ru: 'Набор исследователя подземелий', en: 'Dungeoneer’s Pack' },
  entertainers_pack: { ru: 'Набор артиста', en: 'Entertainer’s Pack' },
  explorers_pack: { ru: 'Набор путешественника', en: 'Explorer’s Pack' },
  healers_kit: { ru: 'Набор целителя', en: 'Healer’s Kit' },
  holy_symbol: { ru: 'Священный символ', en: 'Holy Symbol' },
  leather_armor: { ru: 'Кожаный доспех', en: 'Leather Armor' },
  parchment: { ru: 'Пергамент', en: 'Parchment' },
  pouch: { ru: 'Мешочек', en: 'Pouch' },
  priests_pack: { ru: 'Набор жреца', en: 'Priest’s Pack' },
  quiver: { ru: 'Колчан', en: 'Quiver' },
  robe: { ru: 'Мантия', en: 'Robe' },
  scholars_pack: { ru: 'Набор учёного', en: 'Scholar’s Pack' },
  shield: { ru: 'Щит', en: 'Shield' },
  studded_leather_armor: {
    ru: 'Проклёпанный кожаный доспех',
    en: 'Studded Leather Armor',
  },

  // ── Заклинания (с. 107–175) ──
  acid_arrow: { ru: 'Кислотная стрела', en: 'Acid Arrow' },
  acid_splash: { ru: 'Брызги кислоты', en: 'Acid Splash' },
  aid: { ru: 'Помощь', en: 'Aid' },
  alarm: { ru: 'Сигнал тревоги', en: 'Alarm' },
  alter_self: { ru: 'Изменение себя', en: 'Alter Self' },
  animal_friendship: { ru: 'Дружба с животными', en: 'Animal Friendship' },
  animal_messenger: { ru: 'Животное-посланник', en: 'Animal Messenger' },
  animal_shapes: { ru: 'Звериные обличья', en: 'Animal Shapes' },
  animate_dead: { ru: 'Оживление мертвецов', en: 'Animate Dead' },
  animate_objects: { ru: 'Оживление предметов', en: 'Animate Objects' },
  antilife_shell: { ru: 'Оболочка от живых', en: 'Antilife Shell' },
  antimagic_field: { ru: 'Поле антимагии', en: 'Antimagic Field' },
  antipathy_sympathy: { ru: 'Антипатия/симпатия', en: 'Antipathy/Sympathy' },
  arcane_eye: { ru: 'Волшебный глаз', en: 'Arcane Eye' },
  arcane_hand: { ru: 'Волшебная рука', en: 'Arcane Hand' },
  arcane_lock: { ru: 'Волшебный замок', en: 'Arcane Lock' },
  arcane_sword: { ru: 'Волшебный меч', en: 'Arcane Sword' },
  arcanists_magic_aura: { ru: 'Магическая аура чародея', en: 'Arcanist’s Magic Aura' },
  astral_projection: { ru: 'Астральная проекция', en: 'Astral Projection' },
  augury: { ru: 'Предзнаменование', en: 'Augury' },
  aura_of_life: { ru: 'Аура жизни', en: 'Aura of Life' },
  awaken: { ru: 'Пробуждение разума', en: 'Awaken' },
  bane: { ru: 'Проклятие', en: 'Bane' },
  banishment: { ru: 'Изгнание', en: 'Banishment' },
  barkskin: { ru: 'Кора', en: 'Barkskin' },
  beacon_of_hope: { ru: 'Маяк надежды', en: 'Beacon of Hope' },
  befuddlement: { ru: 'Помутнение рассудка', en: 'Befuddlement' },
  bestow_curse: { ru: 'Наложение проклятия', en: 'Bestow Curse' },
  black_tentacles: { ru: 'Чёрные щупальца', en: 'Black Tentacles' },
  blade_barrier: { ru: 'Стена клинков', en: 'Blade Barrier' },
  bless: { ru: 'Благословение', en: 'Bless' },
  blight: { ru: 'Иссушение', en: 'Blight' },
  blindness_deafness: { ru: 'Слепота/глухота', en: 'Blindness/Deafness' },
  blink: { ru: 'Мерцание', en: 'Blink' },
  blur: { ru: 'Размытый образ', en: 'Blur' },
  burning_hands: { ru: 'Огненные ладони', en: 'Burning Hands' },
  call_lightning: { ru: 'Призыв молнии', en: 'Call Lightning' },
  calm_emotions: { ru: 'Успокоение эмоций', en: 'Calm Emotions' },
  chain_lightning: { ru: 'Цепная молния', en: 'Chain Lightning' },
  charm_monster: { ru: 'Очарование чудовища', en: 'Charm Monster' },
  charm_person: { ru: 'Очарование личности', en: 'Charm Person' },
  chill_touch: { ru: 'Ледяное прикосновение', en: 'Chill Touch' },
  chromatic_orb: { ru: 'Цветной шар', en: 'Chromatic Orb' },
  circle_of_death: { ru: 'Круг смерти', en: 'Circle of Death' },
  clairvoyance: { ru: 'Ясновидение', en: 'Clairvoyance' },
  clone: { ru: 'Клон', en: 'Clone' },
  cloudkill: { ru: 'Смертельное облако', en: 'Cloudkill' },
  color_spray: { ru: 'Цветные брызги', en: 'Color Spray' },
  command: { ru: 'Приказ', en: 'Command' },
  commune: { ru: 'Общение с божеством', en: 'Commune' },
  commune_with_nature: { ru: 'Общение с природой', en: 'Commune with Nature' },
  comprehend_languages: { ru: 'Понимание языков', en: 'Comprehend Languages' },
  compulsion: { ru: 'Принуждение', en: 'Compulsion' },
  cone_of_cold: { ru: 'Конус холода', en: 'Cone of Cold' },
  confusion: { ru: 'Смятение', en: 'Confusion' },
  conjure_animals: { ru: 'Призыв животных', en: 'Conjure Animals' },
  conjure_celestial: { ru: 'Призыв небожителя', en: 'Conjure Celestial' },
  conjure_elemental: { ru: 'Призыв элементаля', en: 'Conjure Elemental' },
  conjure_fey: { ru: 'Призыв феи', en: 'Conjure Fey' },
  conjure_minor_elementals: {
    ru: 'Призыв малых элементалей',
    en: 'Conjure Minor Elementals',
  },
  conjure_woodland_beings: {
    ru: 'Призыв лесных обитателей',
    en: 'Conjure Woodland Beings',
  },
  contact_other_plane: { ru: 'Связь с иным планом', en: 'Contact Other Plane' },
  contagion: { ru: 'Зараза', en: 'Contagion' },
  contingency: { ru: 'Непредвиденный случай', en: 'Contingency' },
  continual_flame: { ru: 'Вечное пламя', en: 'Continual Flame' },
  control_water: { ru: 'Власть над водами', en: 'Control Water' },
  control_weather: { ru: 'Власть над погодой', en: 'Control Weather' },
  counterspell: { ru: 'Контрзаклинание', en: 'Counterspell' },
  create_food_and_water: { ru: 'Создание еды и воды', en: 'Create Food and Water' },
  create_or_destroy_water: {
    ru: 'Создание или уничтожение воды',
    en: 'Create or Destroy Water',
  },
  create_undead: { ru: 'Создание нежити', en: 'Create Undead' },
  creation: { ru: 'Сотворение', en: 'Creation' },
  cure_wounds: { ru: 'Лечение ран', en: 'Cure Wounds' },
  dancing_lights: { ru: 'Пляшущие огоньки', en: 'Dancing Lights' },
  darkness: { ru: 'Тьма', en: 'Darkness' },
  darkvision: { ru: 'Тёмное зрение', en: 'Darkvision' },
  daylight: { ru: 'Дневной свет', en: 'Daylight' },
  death_ward: { ru: 'Защита от смерти', en: 'Death Ward' },
  delayed_blast_fireball: {
    ru: 'Огненный шар замедленного действия',
    en: 'Delayed Blast Fireball',
  },
  demiplane: { ru: 'Полуплан', en: 'Demiplane' },
  detect_evil_and_good: { ru: 'Обнаружение добра и зла', en: 'Detect Evil and Good' },
  detect_magic: { ru: 'Обнаружение магии', en: 'Detect Magic' },
  detect_poison_and_disease: {
    ru: 'Обнаружение яда и болезни',
    en: 'Detect Poison and Disease',
  },
  detect_thoughts: { ru: 'Обнаружение мыслей', en: 'Detect Thoughts' },
  dimension_door: { ru: 'Пространственная дверь', en: 'Dimension Door' },
  disguise_self: { ru: 'Маскировка', en: 'Disguise Self' },
  disintegrate: { ru: 'Распад', en: 'Disintegrate' },
  dispel_evil_and_good: { ru: 'Рассеивание добра и зла', en: 'Dispel Evil and Good' },
  dispel_magic: { ru: 'Рассеивание магии', en: 'Dispel Magic' },
  dissonant_whispers: { ru: 'Диссонирующий шёпот', en: 'Dissonant Whispers' },
  divination: { ru: 'Прорицание', en: 'Divination' },
  divine_favor: { ru: 'Божественная благосклонность', en: 'Divine Favor' },
  divine_smite: { ru: 'Божественная кара', en: 'Divine Smite' },
  divine_word: { ru: 'Божественное слово', en: 'Divine Word' },
  dominate_beast: { ru: 'Подчинение зверя', en: 'Dominate Beast' },
  dominate_monster: { ru: 'Подчинение чудовища', en: 'Dominate Monster' },
  dominate_person: { ru: 'Подчинение личности', en: 'Dominate Person' },
  dragons_breath: { ru: 'Дыхание дракона', en: 'Dragon’s Breath' },
  dream: { ru: 'Сновидение', en: 'Dream' },
  druidcraft: { ru: 'Друидизм', en: 'Druidcraft' },
  earthquake: { ru: 'Землетрясение', en: 'Earthquake' },
  eldritch_blast: { ru: 'Мистический заряд', en: 'Eldritch Blast' },
  elementalism: { ru: 'Стихийные чары', en: 'Elementalism' },
  enhance_ability: { ru: 'Усиление характеристики', en: 'Enhance Ability' },
  enlarge_reduce: { ru: 'Увеличение/уменьшение', en: 'Enlarge/Reduce' },
  ensnaring_strike: { ru: 'Опутывающий удар', en: 'Ensnaring Strike' },
  entangle: { ru: 'Опутывание', en: 'Entangle' },
  enthrall: { ru: 'Пленение', en: 'Enthrall' },
  etherealness: { ru: 'Эфирность', en: 'Etherealness' },
  expeditious_retreat: { ru: 'Стремительное отступление', en: 'Expeditious Retreat' },
  eyebite: { ru: 'Дурной глаз', en: 'Eyebite' },
  fabricate: { ru: 'Изготовление', en: 'Fabricate' },
  faerie_fire: { ru: 'Огонь фей', en: 'Faerie Fire' },
  faithful_hound: { ru: 'Верный пёс', en: 'Faithful Hound' },
  false_life: { ru: 'Ложная жизнь', en: 'False Life' },
  fear: { ru: 'Страх', en: 'Fear' },
  feather_fall: { ru: 'Падение пёрышком', en: 'Feather Fall' },
  find_familiar: { ru: 'Обретение фамильяра', en: 'Find Familiar' },
  find_steed: { ru: 'Обретение скакуна', en: 'Find Steed' },
  find_the_path: { ru: 'Поиск пути', en: 'Find the Path' },
  find_traps: { ru: 'Поиск ловушек', en: 'Find Traps' },
  finger_of_death: { ru: 'Перст смерти', en: 'Finger of Death' },
  fire_bolt: { ru: 'Огненный снаряд', en: 'Fire Bolt' },
  fire_shield: { ru: 'Огненный щит', en: 'Fire Shield' },
  fire_storm: { ru: 'Огненная буря', en: 'Fire Storm' },
  fireball: { ru: 'Огненный шар', en: 'Fireball' },
  flame_blade: { ru: 'Огненный клинок', en: 'Flame Blade' },
  flame_strike: { ru: 'Огненный столб', en: 'Flame Strike' },
  flaming_sphere: { ru: 'Пылающая сфера', en: 'Flaming Sphere' },
  flesh_to_stone: { ru: 'Превращение в камень', en: 'Flesh to Stone' },
  floating_disk: { ru: 'Парящий диск', en: 'Floating Disk' },
  fly: { ru: 'Полёт', en: 'Fly' },
  fog_cloud: { ru: 'Туманное облако', en: 'Fog Cloud' },
  forbiddance: { ru: 'Запрет', en: 'Forbiddance' },
  forcecage: { ru: 'Клетка силы', en: 'Forcecage' },
  foresight: { ru: 'Предвидение', en: 'Foresight' },
  freedom_of_movement: { ru: 'Свобода перемещения', en: 'Freedom of Movement' },
  freezing_sphere: { ru: 'Леденящая сфера', en: 'Freezing Sphere' },
  gaseous_form: { ru: 'Газообразная форма', en: 'Gaseous Form' },
  gate: { ru: 'Врата', en: 'Gate' },
  geas: { ru: 'Гейс', en: 'Geas' },
  gentle_repose: { ru: 'Тихий покой', en: 'Gentle Repose' },
  giant_insect: { ru: 'Гигантское насекомое', en: 'Giant Insect' },
  glibness: { ru: 'Красноречие', en: 'Glibness' },
  globe_of_invulnerability: {
    ru: 'Сфера неуязвимости',
    en: 'Globe of Invulnerability',
  },
  glyph_of_warding: { ru: 'Охранная руна', en: 'Glyph of Warding' },
  goodberry: { ru: 'Ягоды с добрым словом', en: 'Goodberry' },
  grease: { ru: 'Жир', en: 'Grease' },
  greater_invisibility: { ru: 'Высшая невидимость', en: 'Greater Invisibility' },
  greater_restoration: { ru: 'Высшее восстановление', en: 'Greater Restoration' },
  guardian_of_faith: { ru: 'Страж веры', en: 'Guardian of Faith' },
  guards_and_wards: { ru: 'Стражи и охранники', en: 'Guards and Wards' },
  guidance: { ru: 'Указание', en: 'Guidance' },
  guiding_bolt: { ru: 'Указующая стрела', en: 'Guiding Bolt' },
  gust_of_wind: { ru: 'Порыв ветра', en: 'Gust of Wind' },
  hallow: { ru: 'Освящение', en: 'Hallow' },
  hallucinatory_terrain: { ru: 'Мнимый ландшафт', en: 'Hallucinatory Terrain' },
  harm: { ru: 'Вред', en: 'Harm' },
  haste: { ru: 'Спешка', en: 'Haste' },
  heal: { ru: 'Исцеление', en: 'Heal' },
  healing_word: { ru: 'Лечащее слово', en: 'Healing Word' },
  heat_metal: { ru: 'Раскалённый металл', en: 'Heat Metal' },
  hellish_rebuke: { ru: 'Адское возмездие', en: 'Hellish Rebuke' },
  heroes_feast: { ru: 'Пир героев', en: 'Heroes’ Feast' },
  heroism: { ru: 'Героизм', en: 'Heroism' },
  hex: { ru: 'Порча', en: 'Hex' },
  hideous_laughter: { ru: 'Нелепый смех', en: 'Hideous Laughter' },
  hold_monster: { ru: 'Удержание чудовища', en: 'Hold Monster' },
  hold_person: { ru: 'Удержание личности', en: 'Hold Person' },
  holy_aura: { ru: 'Священная аура', en: 'Holy Aura' },
  hunters_mark: { ru: 'Метка охотника', en: 'Hunter’s Mark' },
  hypnotic_pattern: { ru: 'Гипнотический узор', en: 'Hypnotic Pattern' },
  ice_knife: { ru: 'Ледяной нож', en: 'Ice Knife' },
  ice_storm: { ru: 'Ледяная буря', en: 'Ice Storm' },
  identify: { ru: 'Опознание', en: 'Identify' },
  illusory_script: { ru: 'Иллюзорный текст', en: 'Illusory Script' },
  imprisonment: { ru: 'Заточение', en: 'Imprisonment' },
  incendiary_cloud: { ru: 'Зажигательное облако', en: 'Incendiary Cloud' },
  inflict_wounds: { ru: 'Нанесение ран', en: 'Inflict Wounds' },
  insect_plague: { ru: 'Нашествие насекомых', en: 'Insect Plague' },
  instant_summons: { ru: 'Мгновенный призыв', en: 'Instant Summons' },
  invisibility: { ru: 'Невидимость', en: 'Invisibility' },
  irresistible_dance: { ru: 'Неодолимая пляска', en: 'Irresistible Dance' },
  jump: { ru: 'Прыжок', en: 'Jump' },
  knock: { ru: 'Стук', en: 'Knock' },
  legend_lore: { ru: 'Легендарное знание', en: 'Legend Lore' },
  lesser_restoration: { ru: 'Малое восстановление', en: 'Lesser Restoration' },
  levitate: { ru: 'Левитация', en: 'Levitate' },
  light: { ru: 'Свет', en: 'Light' },
  lightning_bolt: { ru: 'Молния', en: 'Lightning Bolt' },
  locate_animals_or_plants: {
    ru: 'Поиск животных или растений',
    en: 'Locate Animals or Plants',
  },
  locate_creature: { ru: 'Поиск существа', en: 'Locate Creature' },
  locate_object: { ru: 'Поиск предмета', en: 'Locate Object' },
  longstrider: { ru: 'Шаг великана', en: 'Longstrider' },
  mage_armor: { ru: 'Доспехи мага', en: 'Mage Armor' },
  mage_hand: { ru: 'Рука мага', en: 'Mage Hand' },
  magic_circle: { ru: 'Магический круг', en: 'Magic Circle' },
  magic_jar: { ru: 'Волшебный сосуд', en: 'Magic Jar' },
  magic_missile: { ru: 'Волшебная стрела', en: 'Magic Missile' },
  magic_mouth: { ru: 'Волшебные уста', en: 'Magic Mouth' },
  magic_weapon: { ru: 'Магическое оружие', en: 'Magic Weapon' },
  magnificent_mansion: { ru: 'Роскошный особняк', en: 'Magnificent Mansion' },
  major_image: { ru: 'Общая иллюзия', en: 'Major Image' },
  mass_cure_wounds: { ru: 'Массовое лечение ран', en: 'Mass Cure Wounds' },
  mass_heal: { ru: 'Массовое исцеление', en: 'Mass Heal' },
  mass_healing_word: { ru: 'Массовое лечащее слово', en: 'Mass Healing Word' },
  mass_suggestion: { ru: 'Массовое внушение', en: 'Mass Suggestion' },
  maze: { ru: 'Лабиринт', en: 'Maze' },
  meld_into_stone: { ru: 'Слияние с камнем', en: 'Meld into Stone' },
  mending: { ru: 'Починка', en: 'Mending' },
  message: { ru: 'Послание', en: 'Message' },
  meteor_swarm: { ru: 'Метеоритный дождь', en: 'Meteor Swarm' },
  mind_blank: { ru: 'Пустой разум', en: 'Mind Blank' },
  mind_spike: { ru: 'Шип разума', en: 'Mind Spike' },
  minor_illusion: { ru: 'Малая иллюзия', en: 'Minor Illusion' },
  mirage_arcane: { ru: 'Волшебный мираж', en: 'Mirage Arcane' },
  mirror_image: { ru: 'Отражения', en: 'Mirror Image' },
  mislead: { ru: 'Обманка', en: 'Mislead' },
  misty_step: { ru: 'Туманный шаг', en: 'Misty Step' },
  modify_memory: { ru: 'Изменение памяти', en: 'Modify Memory' },
  moonbeam: { ru: 'Лунный луч', en: 'Moonbeam' },
  move_earth: { ru: 'Движение земли', en: 'Move Earth' },
  nondetection: { ru: 'Необнаружимость', en: 'Nondetection' },
  pass_without_trace: { ru: 'Проход без следа', en: 'Pass without Trace' },
  passwall: { ru: 'Проход сквозь стену', en: 'Passwall' },
  phantasmal_force: { ru: 'Фантомная сила', en: 'Phantasmal Force' },
  phantasmal_killer: { ru: 'Фантомный убийца', en: 'Phantasmal Killer' },
  phantom_steed: { ru: 'Фантомный скакун', en: 'Phantom Steed' },
  planar_ally: { ru: 'Союзник с других планов', en: 'Planar Ally' },
  planar_binding: { ru: 'Планарное пленение', en: 'Planar Binding' },
  plane_shift: { ru: 'Переход между планами', en: 'Plane Shift' },
  plant_growth: { ru: 'Рост растений', en: 'Plant Growth' },
  poison_spray: { ru: 'Ядовитые брызги', en: 'Poison Spray' },
  polymorph: { ru: 'Превращение', en: 'Polymorph' },
  power_word_heal: { ru: 'Слово силы: исцеление', en: 'Power Word Heal' },
  power_word_kill: { ru: 'Слово силы: смерть', en: 'Power Word Kill' },
  power_word_stun: { ru: 'Слово силы: оглушение', en: 'Power Word Stun' },
  prayer_of_healing: { ru: 'Молитва лечения', en: 'Prayer of Healing' },
  prestidigitation: { ru: 'Фокусы', en: 'Prestidigitation' },
  prismatic_spray: { ru: 'Радужные брызги', en: 'Prismatic Spray' },
  prismatic_wall: { ru: 'Радужная стена', en: 'Prismatic Wall' },
  private_sanctum: { ru: 'Личное убежище', en: 'Private Sanctum' },
  produce_flame: { ru: 'Сотворение пламени', en: 'Produce Flame' },
  programmed_illusion: { ru: 'Заданная иллюзия', en: 'Programmed Illusion' },
  project_image: { ru: 'Проекция образа', en: 'Project Image' },
  protection_from_energy: { ru: 'Защита от энергии', en: 'Protection from Energy' },
  protection_from_evil_and_good: {
    ru: 'Защита от добра и зла',
    en: 'Protection from Evil and Good',
  },
  protection_from_poison: { ru: 'Защита от яда', en: 'Protection from Poison' },
  purify_food_and_drink: { ru: 'Очищение еды и питья', en: 'Purify Food and Drink' },
  raise_dead: { ru: 'Воскрешение', en: 'Raise Dead' },
  ray_of_enfeeblement: { ru: 'Луч слабости', en: 'Ray of Enfeeblement' },
  ray_of_frost: { ru: 'Луч холода', en: 'Ray of Frost' },
  ray_of_sickness: { ru: 'Луч болезни', en: 'Ray of Sickness' },
  regenerate: { ru: 'Регенерация', en: 'Regenerate' },
  reincarnate: { ru: 'Перевоплощение', en: 'Reincarnate' },
  remove_curse: { ru: 'Снятие проклятия', en: 'Remove Curse' },
  resilient_sphere: { ru: 'Прочная сфера', en: 'Resilient Sphere' },
  resistance: { ru: 'Сопротивление', en: 'Resistance' },
  resurrection: { ru: 'Полное воскрешение', en: 'Resurrection' },
  reverse_gravity: { ru: 'Обратная гравитация', en: 'Reverse Gravity' },
  revivify: { ru: 'Оживление', en: 'Revivify' },
  rope_trick: { ru: 'Фокус с верёвкой', en: 'Rope Trick' },
  sacred_flame: { ru: 'Священное пламя', en: 'Sacred Flame' },
  sanctuary: { ru: 'Убежище', en: 'Sanctuary' },
  scorching_ray: { ru: 'Испепеляющий луч', en: 'Scorching Ray' },
  scrying: { ru: 'Наблюдение', en: 'Scrying' },
  searing_smite: { ru: 'Опаляющая кара', en: 'Searing Smite' },
  secret_chest: { ru: 'Тайный сундук', en: 'Secret Chest' },
  see_invisibility: { ru: 'Видение невидимого', en: 'See Invisibility' },
  seeming: { ru: 'Видимость', en: 'Seeming' },
  sending: { ru: 'Весть', en: 'Sending' },
  sequester: { ru: 'Изъятие', en: 'Sequester' },
  shapechange: { ru: 'Смена облика', en: 'Shapechange' },
  shatter: { ru: 'Дробление', en: 'Shatter' },
  shield_of_faith: { ru: 'Щит веры', en: 'Shield of Faith' },
  shield_spell: { ru: 'Щит', en: 'Shield' },
  shillelagh: { ru: 'Дубинка', en: 'Shillelagh' },
  shining_smite: { ru: 'Сияющая кара', en: 'Shining Smite' },
  shocking_grasp: { ru: 'Электрическое прикосновение', en: 'Shocking Grasp' },
  silence: { ru: 'Тишина', en: 'Silence' },
  silent_image: { ru: 'Безмолвный образ', en: 'Silent Image' },
  simulacrum: { ru: 'Симулякр', en: 'Simulacrum' },
  sleep: { ru: 'Усыпление', en: 'Sleep' },
  sleet_storm: { ru: 'Ледяной шторм', en: 'Sleet Storm' },
  slow: { ru: 'Замедление', en: 'Slow' },
  sorcerous_burst: { ru: 'Чародейский всплеск', en: 'Sorcerous Burst' },
  spare_the_dying: { ru: 'Уход за умирающим', en: 'Spare the Dying' },
  speak_with_animals: { ru: 'Разговор с животными', en: 'Speak with Animals' },
  speak_with_dead: { ru: 'Разговор с мёртвым', en: 'Speak with Dead' },
  speak_with_plants: { ru: 'Разговор с растениями', en: 'Speak with Plants' },
  spider_climb: { ru: 'Паучье лазание', en: 'Spider Climb' },
  spike_growth: { ru: 'Шипы', en: 'Spike Growth' },
  spirit_guardians: { ru: 'Духовные стражи', en: 'Spirit Guardians' },
  spiritual_weapon: { ru: 'Духовное оружие', en: 'Spiritual Weapon' },
  starry_wisp: { ru: 'Звёздная искра', en: 'Starry Wisp' },
  stinking_cloud: { ru: 'Зловонное облако', en: 'Stinking Cloud' },
  stone_shape: { ru: 'Формование камня', en: 'Stone Shape' },
  stoneskin: { ru: 'Каменная кожа', en: 'Stoneskin' },
  storm_of_vengeance: { ru: 'Буря возмездия', en: 'Storm of Vengeance' },
  suggestion: { ru: 'Внушение', en: 'Suggestion' },
  summon_dragon: { ru: 'Призыв дракона', en: 'Summon Dragon' },
  sunbeam: { ru: 'Солнечный луч', en: 'Sunbeam' },
  sunburst: { ru: 'Солнечный взрыв', en: 'Sunburst' },
  symbol: { ru: 'Символ', en: 'Symbol' },
  telekinesis: { ru: 'Телекинез', en: 'Telekinesis' },
  telepathic_bond: { ru: 'Телепатическая связь', en: 'Telepathic Bond' },
  teleport: { ru: 'Телепортация', en: 'Teleport' },
  teleportation_circle: { ru: 'Круг телепортации', en: 'Teleportation Circle' },
  thaumaturgy: { ru: 'Чудотворство', en: 'Thaumaturgy' },
  thunderwave: { ru: 'Волна грома', en: 'Thunderwave' },
  time_stop: { ru: 'Остановка времени', en: 'Time Stop' },
  tiny_hut: { ru: 'Хижина', en: 'Tiny Hut' },
  tongues: { ru: 'Языки', en: 'Tongues' },
  transport_via_plants: { ru: 'Переход через растения', en: 'Transport via Plants' },
  tree_stride: { ru: 'Древесный путь', en: 'Tree Stride' },
  true_polymorph: { ru: 'Истинное превращение', en: 'True Polymorph' },
  true_resurrection: { ru: 'Истинное воскрешение', en: 'True Resurrection' },
  true_seeing: { ru: 'Истинное зрение', en: 'True Seeing' },
  true_strike: { ru: 'Верный удар', en: 'True Strike' },
  tsunami: { ru: 'Цунами', en: 'Tsunami' },
  unseen_servant: { ru: 'Невидимый слуга', en: 'Unseen Servant' },
  vampiric_touch: { ru: 'Прикосновение вампира', en: 'Vampiric Touch' },
  vicious_mockery: { ru: 'Злая насмешка', en: 'Vicious Mockery' },
  vitriolic_sphere: { ru: 'Едкая сфера', en: 'Vitriolic Sphere' },
  wall_of_fire: { ru: 'Огненная стена', en: 'Wall of Fire' },
  wall_of_force: { ru: 'Стена силы', en: 'Wall of Force' },
  wall_of_ice: { ru: 'Ледяная стена', en: 'Wall of Ice' },
  wall_of_stone: { ru: 'Каменная стена', en: 'Wall of Stone' },
  wall_of_thorns: { ru: 'Стена шипов', en: 'Wall of Thorns' },
  warding_bond: { ru: 'Оберегающая связь', en: 'Warding Bond' },
  water_breathing: { ru: 'Подводное дыхание', en: 'Water Breathing' },
  water_walk: { ru: 'Хождение по воде', en: 'Water Walk' },
  web: { ru: 'Паутина', en: 'Web' },
  weird: { ru: 'Кошмар', en: 'Weird' },
  wind_walk: { ru: 'Хождение по ветру', en: 'Wind Walk' },
  wind_wall: { ru: 'Стена ветра', en: 'Wind Wall' },
  wish: { ru: 'Исполнение желаний', en: 'Wish' },
  word_of_recall: { ru: 'Слово возврата', en: 'Word of Recall' },
  zone_of_truth: { ru: 'Зона правды', en: 'Zone of Truth' },

  // ── Монстры (с. 255–361, D50) ──
  //
  // Мастер выбирает монстра из справочника, а не печатает имя (D50),
  // поэтому непереведённое название здесь — не мелочь: это английская
  // строка в русском списке, по которому и выбирают. Переведены все
  // 329, а не выборка: справочник показывает список целиком.
  //
  // Имена собственные передаются транслитерацией («Аболет», «Балор»),
  // описательные — по смыслу («Совомедведь», «Топороклюв»). Уточнения
  // роли остаются уточнениями: «Bugbear Warrior» — «Багбир-воин»,
  // а не «Воин-багбир»: в списке сначала читают, кто это, и лишь потом
  // чем занят.
  aboleth: { ru: 'Аболет', en: 'Aboleth' },
  adult_black_dragon: { ru: 'Взрослый чёрный дракон', en: 'Adult Black Dragon' },
  adult_blue_dragon: { ru: 'Взрослый синий дракон', en: 'Adult Blue Dragon' },
  adult_brass_dragon: { ru: 'Взрослый латунный дракон', en: 'Adult Brass Dragon' },
  adult_bronze_dragon: { ru: 'Взрослый бронзовый дракон', en: 'Adult Bronze Dragon' },
  adult_copper_dragon: { ru: 'Взрослый медный дракон', en: 'Adult Copper Dragon' },
  adult_gold_dragon: { ru: 'Взрослый золотой дракон', en: 'Adult Gold Dragon' },
  adult_green_dragon: { ru: 'Взрослый зелёный дракон', en: 'Adult Green Dragon' },
  adult_red_dragon: { ru: 'Взрослый красный дракон', en: 'Adult Red Dragon' },
  adult_silver_dragon: { ru: 'Взрослый серебряный дракон', en: 'Adult Silver Dragon' },
  adult_white_dragon: { ru: 'Взрослый белый дракон', en: 'Adult White Dragon' },
  air_elemental: { ru: 'Воздушный элементаль', en: 'Air Elemental' },
  allosaurus: { ru: 'Аллозавр', en: 'Allosaurus' },
  ancient_black_dragon: { ru: 'Древний чёрный дракон', en: 'Ancient Black Dragon' },
  ancient_blue_dragon: { ru: 'Древний синий дракон', en: 'Ancient Blue Dragon' },
  ancient_brass_dragon: { ru: 'Древний латунный дракон', en: 'Ancient Brass Dragon' },
  ancient_bronze_dragon: {
    ru: 'Древний бронзовый дракон',
    en: 'Ancient Bronze Dragon',
  },
  ancient_copper_dragon: { ru: 'Древний медный дракон', en: 'Ancient Copper Dragon' },
  ancient_gold_dragon: { ru: 'Древний золотой дракон', en: 'Ancient Gold Dragon' },
  ancient_green_dragon: { ru: 'Древний зелёный дракон', en: 'Ancient Green Dragon' },
  ancient_red_dragon: { ru: 'Древний красный дракон', en: 'Ancient Red Dragon' },
  ancient_silver_dragon: {
    ru: 'Древний серебряный дракон',
    en: 'Ancient Silver Dragon',
  },
  ancient_white_dragon: { ru: 'Древний белый дракон', en: 'Ancient White Dragon' },
  animated_armor: { ru: 'Оживший доспех', en: 'Animated Armor' },
  animated_flying_sword: { ru: 'Оживший летающий меч', en: 'Animated Flying Sword' },
  animated_rug_of_smothering: {
    ru: 'Оживший душащий ковёр',
    en: 'Animated Rug of Smothering',
  },
  ankheg: { ru: 'Анкег', en: 'Ankheg' },
  ankylosaurus: { ru: 'Анкилозавр', en: 'Ankylosaurus' },
  ape: { ru: 'Обезьяна', en: 'Ape' },
  archelon: { ru: 'Архелон', en: 'Archelon' },
  archmage: { ru: 'Архимаг', en: 'Archmage' },
  assassin: { ru: 'Убийца', en: 'Assassin' },
  awakened_shrub: { ru: 'Пробуждённый куст', en: 'Awakened Shrub' },
  awakened_tree: { ru: 'Пробуждённое дерево', en: 'Awakened Tree' },
  axe_beak: { ru: 'Топороклюв', en: 'Axe Beak' },
  azer_sentinel: { ru: 'Азер-страж', en: 'Azer Sentinel' },
  baboon: { ru: 'Бабуин', en: 'Baboon' },
  badger: { ru: 'Барсук', en: 'Badger' },
  balor: { ru: 'Балор', en: 'Balor' },
  bandit: { ru: 'Разбойник', en: 'Bandit' },
  bandit_captain: { ru: 'Главарь разбойников', en: 'Bandit Captain' },
  barbed_devil: { ru: 'Шипастый дьявол', en: 'Barbed Devil' },
  basilisk: { ru: 'Василиск', en: 'Basilisk' },
  bat: { ru: 'Летучая мышь', en: 'Bat' },
  bearded_devil: { ru: 'Бородатый дьявол', en: 'Bearded Devil' },
  behir: { ru: 'Бехир', en: 'Behir' },
  berserker: { ru: 'Берсерк', en: 'Berserker' },
  black_bear: { ru: 'Чёрный медведь', en: 'Black Bear' },
  black_dragon_wyrmling: { ru: 'Чёрный дракончик', en: 'Black Dragon Wyrmling' },
  black_pudding: { ru: 'Чёрная слизь', en: 'Black Pudding' },
  blink_dog: { ru: 'Мерцающий пёс', en: 'Blink Dog' },
  blood_hawk: { ru: 'Кровавый ястреб', en: 'Blood Hawk' },
  blue_dragon_wyrmling: { ru: 'Синий дракончик', en: 'Blue Dragon Wyrmling' },
  boar: { ru: 'Кабан', en: 'Boar' },
  bone_devil: { ru: 'Костяной дьявол', en: 'Bone Devil' },
  brass_dragon_wyrmling: { ru: 'Латунный дракончик', en: 'Brass Dragon Wyrmling' },
  bronze_dragon_wyrmling: { ru: 'Бронзовый дракончик', en: 'Bronze Dragon Wyrmling' },
  brown_bear: { ru: 'Бурый медведь', en: 'Brown Bear' },
  bugbear_stalker: { ru: 'Багбир-загонщик', en: 'Bugbear Stalker' },
  bugbear_warrior: { ru: 'Багбир-воин', en: 'Bugbear Warrior' },
  bulette: { ru: 'Булетта', en: 'Bulette' },
  camel_monster: { ru: 'Верблюд', en: 'Camel' },
  cat: { ru: 'Кошка', en: 'Cat' },
  centaur_trooper: { ru: 'Кентавр-солдат', en: 'Centaur Trooper' },
  chain_devil: { ru: 'Цепной дьявол', en: 'Chain Devil' },
  chimera: { ru: 'Химера', en: 'Chimera' },
  chuul: { ru: 'Чуул', en: 'Chuul' },
  clay_golem: { ru: 'Глиняный голем', en: 'Clay Golem' },
  cloaker: { ru: 'Плащевик', en: 'Cloaker' },
  cloud_giant: { ru: 'Облачный великан', en: 'Cloud Giant' },
  cockatrice: { ru: 'Кокатрикс', en: 'Cockatrice' },
  commoner: { ru: 'Простолюдин', en: 'Commoner' },
  constrictor_snake: { ru: 'Змея-констриктор', en: 'Constrictor Snake' },
  copper_dragon_wyrmling: { ru: 'Медный дракончик', en: 'Copper Dragon Wyrmling' },
  couatl: { ru: 'Куатль', en: 'Couatl' },
  crab: { ru: 'Краб', en: 'Crab' },
  crocodile: { ru: 'Крокодил', en: 'Crocodile' },
  cultist: { ru: 'Культист', en: 'Cultist' },
  cultist_fanatic: { ru: 'Культист-фанатик', en: 'Cultist Fanatic' },
  darkmantle: { ru: 'Тёмный плащ', en: 'Darkmantle' },
  death_dog: { ru: 'Пёс смерти', en: 'Death Dog' },
  deer: { ru: 'Олень', en: 'Deer' },
  deva: { ru: 'Дэва', en: 'Deva' },
  dire_wolf: { ru: 'Лютый волк', en: 'Dire Wolf' },
  djinni: { ru: 'Джинн', en: 'Djinni' },
  doppelganger: { ru: 'Доппельгангер', en: 'Doppelganger' },
  draft_horse: { ru: 'Тягловая лошадь', en: 'Draft Horse' },
  dragon_turtle: { ru: 'Драконья черепаха', en: 'Dragon Turtle' },
  dretch: { ru: 'Дреч', en: 'Dretch' },
  drider: { ru: 'Дридер', en: 'Drider' },
  druid_monster: { ru: 'Друид', en: 'Druid' },
  dryad: { ru: 'Дриада', en: 'Dryad' },
  dust_mephit: { ru: 'Пыльный мефит', en: 'Dust Mephit' },
  eagle: { ru: 'Орёл', en: 'Eagle' },
  earth_elemental: { ru: 'Земляной элементаль', en: 'Earth Elemental' },
  efreeti: { ru: 'Ифрит', en: 'Efreeti' },
  elephant_monster: { ru: 'Слон', en: 'Elephant' },
  elk: { ru: 'Лось', en: 'Elk' },
  erinyes: { ru: 'Эриния', en: 'Erinyes' },
  ettercap: { ru: 'Эттеркап', en: 'Ettercap' },
  ettin: { ru: 'Эттин', en: 'Ettin' },
  fire_elemental: { ru: 'Огненный элементаль', en: 'Fire Elemental' },
  fire_giant: { ru: 'Огненный великан', en: 'Fire Giant' },
  flesh_golem: { ru: 'Плотяной голем', en: 'Flesh Golem' },
  flying_snake: { ru: 'Летучая змея', en: 'Flying Snake' },
  frog: { ru: 'Лягушка', en: 'Frog' },
  frost_giant: { ru: 'Ледяной великан', en: 'Frost Giant' },
  gargoyle: { ru: 'Горгулья', en: 'Gargoyle' },
  gelatinous_cube: { ru: 'Желатиновый куб', en: 'Gelatinous Cube' },
  ghast: { ru: 'Гаст', en: 'Ghast' },
  ghost: { ru: 'Призрак', en: 'Ghost' },
  ghoul: { ru: 'Упырь', en: 'Ghoul' },
  giant_ape: { ru: 'Гигантская обезьяна', en: 'Giant Ape' },
  giant_badger: { ru: 'Гигантский барсук', en: 'Giant Badger' },
  giant_bat: { ru: 'Гигантская летучая мышь', en: 'Giant Bat' },
  giant_boar: { ru: 'Гигантский кабан', en: 'Giant Boar' },
  giant_centipede: { ru: 'Гигантская многоножка', en: 'Giant Centipede' },
  giant_constrictor_snake: {
    ru: 'Гигантская змея-констриктор',
    en: 'Giant Constrictor Snake',
  },
  giant_crab: { ru: 'Гигантский краб', en: 'Giant Crab' },
  giant_crocodile: { ru: 'Гигантский крокодил', en: 'Giant Crocodile' },
  giant_eagle: { ru: 'Гигантский орёл', en: 'Giant Eagle' },
  giant_elk: { ru: 'Гигантский лось', en: 'Giant Elk' },
  giant_fire_beetle: { ru: 'Гигантский огненный жук', en: 'Giant Fire Beetle' },
  giant_frog: { ru: 'Гигантская лягушка', en: 'Giant Frog' },
  giant_goat: { ru: 'Гигантский козёл', en: 'Giant Goat' },
  giant_hyena: { ru: 'Гигантская гиена', en: 'Giant Hyena' },
  giant_lizard: { ru: 'Гигантская ящерица', en: 'Giant Lizard' },
  giant_octopus: { ru: 'Гигантский осьминог', en: 'Giant Octopus' },
  giant_owl: { ru: 'Гигантская сова', en: 'Giant Owl' },
  giant_rat: { ru: 'Гигантская крыса', en: 'Giant Rat' },
  giant_scorpion: { ru: 'Гигантский скорпион', en: 'Giant Scorpion' },
  giant_seahorse: { ru: 'Гигантский морской конёк', en: 'Giant Seahorse' },
  giant_shark: { ru: 'Гигантская акула', en: 'Giant Shark' },
  giant_spider: { ru: 'Гигантский паук', en: 'Giant Spider' },
  giant_toad: { ru: 'Гигантская жаба', en: 'Giant Toad' },
  giant_venomous_snake: { ru: 'Гигантская ядовитая змея', en: 'Giant Venomous Snake' },
  giant_vulture: { ru: 'Гигантский стервятник', en: 'Giant Vulture' },
  giant_wasp: { ru: 'Гигантская оса', en: 'Giant Wasp' },
  giant_weasel: { ru: 'Гигантская ласка', en: 'Giant Weasel' },
  giant_wolf_spider: { ru: 'Гигантский паук-волк', en: 'Giant Wolf Spider' },
  gibbering_mouther: { ru: 'Бормочущий рот', en: 'Gibbering Mouther' },
  glabrezu: { ru: 'Глабрезу', en: 'Glabrezu' },
  gladiator: { ru: 'Гладиатор', en: 'Gladiator' },
  gnoll_warrior: { ru: 'Гнолл-воин', en: 'Gnoll Warrior' },
  goat: { ru: 'Козёл', en: 'Goat' },
  goblin_boss: { ru: 'Гоблин-вожак', en: 'Goblin Boss' },
  goblin_minion: { ru: 'Гоблин-прихвостень', en: 'Goblin Minion' },
  goblin_warrior: { ru: 'Гоблин-воин', en: 'Goblin Warrior' },
  gold_dragon_wyrmling: { ru: 'Золотой дракончик', en: 'Gold Dragon Wyrmling' },
  gorgon: { ru: 'Горгона', en: 'Gorgon' },
  gray_ooze: { ru: 'Серая слизь', en: 'Gray Ooze' },
  green_dragon_wyrmling: { ru: 'Зелёный дракончик', en: 'Green Dragon Wyrmling' },
  green_hag: { ru: 'Зелёная карга', en: 'Green Hag' },
  grick: { ru: 'Грик', en: 'Grick' },
  griffon: { ru: 'Грифон', en: 'Griffon' },
  grimlock: { ru: 'Гримлок', en: 'Grimlock' },
  guard: { ru: 'Стражник', en: 'Guard' },
  guard_captain: { ru: 'Капитан стражи', en: 'Guard Captain' },
  guardian_naga: { ru: 'Нага-хранительница', en: 'Guardian Naga' },
  half_dragon: { ru: 'Полудракон', en: 'Half-Dragon' },
  harpy: { ru: 'Гарпия', en: 'Harpy' },
  hawk: { ru: 'Ястреб', en: 'Hawk' },
  hell_hound: { ru: 'Адская гончая', en: 'Hell Hound' },
  hezrou: { ru: 'Хезроу', en: 'Hezrou' },
  hill_giant: { ru: 'Холмовой великан', en: 'Hill Giant' },
  hippogriff: { ru: 'Гиппогриф', en: 'Hippogriff' },
  hippopotamus: { ru: 'Бегемот', en: 'Hippopotamus' },
  hobgoblin_captain: { ru: 'Хобгоблин-капитан', en: 'Hobgoblin Captain' },
  hobgoblin_warrior: { ru: 'Хобгоблин-воин', en: 'Hobgoblin Warrior' },
  homunculus: { ru: 'Гомункул', en: 'Homunculus' },
  horned_devil: { ru: 'Рогатый дьявол', en: 'Horned Devil' },
  hunter_shark: { ru: 'Акула-охотник', en: 'Hunter Shark' },
  hydra: { ru: 'Гидра', en: 'Hydra' },
  hyena: { ru: 'Гиена', en: 'Hyena' },
  ice_devil: { ru: 'Ледяной дьявол', en: 'Ice Devil' },
  ice_mephit: { ru: 'Ледяной мефит', en: 'Ice Mephit' },
  imp: { ru: 'Бес', en: 'Imp' },
  incubus: { ru: 'Инкуб', en: 'Incubus' },
  invisible_stalker: { ru: 'Невидимый охотник', en: 'Invisible Stalker' },
  iron_golem: { ru: 'Железный голем', en: 'Iron Golem' },
  iron_golem_p299: { ru: 'Железный голем (с. 299, статблок рыцаря)', en: 'Iron Golem' },
  jackal: { ru: 'Шакал', en: 'Jackal' },
  killer_whale: { ru: 'Косатка', en: 'Killer Whale' },
  kobold_warrior: { ru: 'Кобольд-воин', en: 'Kobold Warrior' },
  kraken: { ru: 'Кракен', en: 'Kraken' },
  lamia: { ru: 'Ламия', en: 'Lamia' },
  lemure: { ru: 'Лемур', en: 'Lemure' },
  lich: { ru: 'Лич', en: 'Lich' },
  lion: { ru: 'Лев', en: 'Lion' },
  lizard: { ru: 'Ящерица', en: 'Lizard' },
  mage: { ru: 'Маг', en: 'Mage' },
  magma_mephit: { ru: 'Магмовый мефит', en: 'Magma Mephit' },
  magmin: { ru: 'Магмин', en: 'Magmin' },
  mammoth: { ru: 'Мамонт', en: 'Mammoth' },
  manticore: { ru: 'Мантикора', en: 'Manticore' },
  marilith: { ru: 'Марилит', en: 'Marilith' },
  mastiff_monster: { ru: 'Мастиф', en: 'Mastiff' },
  medusa: { ru: 'Медуза', en: 'Medusa' },
  merfolk_skirmisher: { ru: 'Мерфолк-застрельщик', en: 'Merfolk Skirmisher' },
  merrow: { ru: 'Мерроу', en: 'Merrow' },
  mimic: { ru: 'Мимик', en: 'Mimic' },
  minotaur_of_baphomet: { ru: 'Минотавр Бафомета', en: 'Minotaur of Baphomet' },
  minotaur_skeleton: { ru: 'Скелет минотавра', en: 'Minotaur Skeleton' },
  mule_monster: { ru: 'Мул', en: 'Mule' },
  mummy: { ru: 'Мумия', en: 'Mummy' },
  mummy_lord: { ru: 'Владыка мумий', en: 'Mummy Lord' },
  nalfeshnee: { ru: 'Налфешни', en: 'Nalfeshnee' },
  night_hag: { ru: 'Ночная карга', en: 'Night Hag' },
  nightmare: { ru: 'Кошмарный конь', en: 'Nightmare' },
  noble: { ru: 'Дворянин', en: 'Noble' },
  ochre_jelly: { ru: 'Охряное желе', en: 'Ochre Jelly' },
  ogre: { ru: 'Огр', en: 'Ogre' },
  ogre_zombie: { ru: 'Огр-зомби', en: 'Ogre Zombie' },
  oni: { ru: 'Они', en: 'Oni' },
  otyugh: { ru: 'Отиг', en: 'Otyugh' },
  owl: { ru: 'Сова', en: 'Owl' },
  owlbear: { ru: 'Совомедведь', en: 'Owlbear' },
  panther: { ru: 'Пантера', en: 'Panther' },
  pegasus: { ru: 'Пегас', en: 'Pegasus' },
  phase_spider: { ru: 'Фазовый паук', en: 'Phase Spider' },
  piranha: { ru: 'Пиранья', en: 'Piranha' },
  pirate: { ru: 'Пират', en: 'Pirate' },
  pirate_captain: { ru: 'Капитан пиратов', en: 'Pirate Captain' },
  pit_fiend: { ru: 'Исчадие ямы', en: 'Pit Fiend' },
  planetar: { ru: 'Планетар', en: 'Planetar' },
  plesiosaurus: { ru: 'Плезиозавр', en: 'Plesiosaurus' },
  polar_bear: { ru: 'Белый медведь', en: 'Polar Bear' },
  pony_monster: { ru: 'Пони', en: 'Pony' },
  priest: { ru: 'Жрец', en: 'Priest' },
  priest_acolyte: { ru: 'Послушник', en: 'Priest Acolyte' },
  pseudodragon: { ru: 'Псевдодракон', en: 'Pseudodragon' },
  pteranodon: { ru: 'Птеранодон', en: 'Pteranodon' },
  purple_worm: { ru: 'Пурпурный червь', en: 'Purple Worm' },
  quasit: { ru: 'Квазит', en: 'Quasit' },
  rakshasa: { ru: 'Ракшас', en: 'Rakshasa' },
  rat: { ru: 'Крыса', en: 'Rat' },
  raven: { ru: 'Ворон', en: 'Raven' },
  red_dragon_wyrmling: { ru: 'Красный дракончик', en: 'Red Dragon Wyrmling' },
  reef_shark: { ru: 'Рифовая акула', en: 'Reef Shark' },
  remorhaz: { ru: 'Реморхаз', en: 'Remorhaz' },
  rhinoceros: { ru: 'Носорог', en: 'Rhinoceros' },
  riding_horse: { ru: 'Верховая лошадь', en: 'Riding Horse' },
  roc: { ru: 'Рух', en: 'Roc' },
  roper: { ru: 'Ропер', en: 'Roper' },
  rust_monster: { ru: 'Ржавник', en: 'Rust Monster' },
  saber_toothed_tiger: { ru: 'Саблезубый тигр', en: 'Saber-Toothed Tiger' },
  sahuagin_warrior: { ru: 'Сахуагин-воин', en: 'Sahuagin Warrior' },
  salamander: { ru: 'Саламандра', en: 'Salamander' },
  satyr: { ru: 'Сатир', en: 'Satyr' },
  scorpion: { ru: 'Скорпион', en: 'Scorpion' },
  scout: { ru: 'Разведчик', en: 'Scout' },
  sea_hag: { ru: 'Морская карга', en: 'Sea Hag' },
  seahorse: { ru: 'Морской конёк', en: 'Seahorse' },
  shadow: { ru: 'Тень', en: 'Shadow' },
  shambling_mound: { ru: 'Бродячий холм', en: 'Shambling Mound' },
  shield_guardian: { ru: 'Страж щита', en: 'Shield Guardian' },
  shrieker_fungus: { ru: 'Визгун', en: 'Shrieker Fungus' },
  silver_dragon_wyrmling: { ru: 'Серебряный дракончик', en: 'Silver Dragon Wyrmling' },
  skeleton: { ru: 'Скелет', en: 'Skeleton' },
  solar: { ru: 'Солар', en: 'Solar' },
  specter: { ru: 'Спектр', en: 'Specter' },
  sphinx_of_lore: { ru: 'Сфинкс знания', en: 'Sphinx of Lore' },
  sphinx_of_valor: { ru: 'Сфинкс доблести', en: 'Sphinx of Valor' },
  sphinx_of_wonder: { ru: 'Сфинкс чуда', en: 'Sphinx of Wonder' },
  spider: { ru: 'Паук', en: 'Spider' },
  spirit_naga: { ru: 'Духовная нага', en: 'Spirit Naga' },
  sprite: { ru: 'Спрайт', en: 'Sprite' },
  spy: { ru: 'Шпион', en: 'Spy' },
  steam_mephit: { ru: 'Паровой мефит', en: 'Steam Mephit' },
  stirge: { ru: 'Стирж', en: 'Stirge' },
  stone_giant: { ru: 'Каменный великан', en: 'Stone Giant' },
  stone_golem: { ru: 'Каменный голем', en: 'Stone Golem' },
  storm_giant: { ru: 'Штормовой великан', en: 'Storm Giant' },
  succubus: { ru: 'Суккуб', en: 'Succubus' },
  swarm_of_bats: { ru: 'Рой летучих мышей', en: 'Swarm of Bats' },
  swarm_of_crawling_claws: { ru: 'Рой ползучих лап', en: 'Swarm of Crawling Claws' },
  swarm_of_insects: { ru: 'Рой насекомых', en: 'Swarm of Insects' },
  swarm_of_piranhas: { ru: 'Рой пираний', en: 'Swarm of Piranhas' },
  swarm_of_rats: { ru: 'Рой крыс', en: 'Swarm of Rats' },
  swarm_of_ravens: { ru: 'Стая воронов', en: 'Swarm of Ravens' },
  swarm_of_venomous_snakes: {
    ru: 'Клубок ядовитых змей',
    en: 'Swarm of Venomous Snakes',
  },
  tarrasque: { ru: 'Тарраск', en: 'Tarrasque' },
  tiger: { ru: 'Тигр', en: 'Tiger' },
  tough: { ru: 'Головорез', en: 'Tough' },
  tough_boss: { ru: 'Главарь головорезов', en: 'Tough Boss' },
  treant: { ru: 'Древень', en: 'Treant' },
  triceratops: { ru: 'Трицератопс', en: 'Triceratops' },
  troll: { ru: 'Тролль', en: 'Troll' },
  troll_limb: { ru: 'Конечность тролля', en: 'Troll Limb' },
  tyrannosaurus_rex: { ru: 'Тираннозавр', en: 'Tyrannosaurus Rex' },
  unicorn: { ru: 'Единорог', en: 'Unicorn' },
  vampire: { ru: 'Вампир', en: 'Vampire' },
  vampire_familiar: { ru: 'Фамильяр вампира', en: 'Vampire Familiar' },
  vampire_spawn: { ru: 'Отродье вампира', en: 'Vampire Spawn' },
  venomous_snake: { ru: 'Ядовитая змея', en: 'Venomous Snake' },
  violet_fungus: { ru: 'Фиолетовый гриб', en: 'Violet Fungus' },
  vrock: { ru: 'Врок', en: 'Vrock' },
  vulture: { ru: 'Стервятник', en: 'Vulture' },
  warhorse_monster: { ru: 'Боевой конь', en: 'Warhorse' },
  warhorse_skeleton: { ru: 'Скелет боевого коня', en: 'Warhorse Skeleton' },
  warrior_infantry: { ru: 'Пехотинец', en: 'Warrior Infantry' },
  warrior_veteran: { ru: 'Ветеран', en: 'Warrior Veteran' },
  water_elemental: { ru: 'Водяной элементаль', en: 'Water Elemental' },
  weasel: { ru: 'Ласка', en: 'Weasel' },
  werebear: { ru: 'Медведь-оборотень', en: 'Werebear' },
  wereboar: { ru: 'Кабан-оборотень', en: 'Wereboar' },
  wererat: { ru: 'Крыса-оборотень', en: 'Wererat' },
  weretiger: { ru: 'Тигр-оборотень', en: 'Weretiger' },
  werewolf: { ru: 'Волк-оборотень', en: 'Werewolf' },
  white_dragon_wyrmling: { ru: 'Белый дракончик', en: 'White Dragon Wyrmling' },
  wight: { ru: 'Умертвие', en: 'Wight' },
  will_o_wisp: { ru: 'Блуждающий огонёк', en: 'Will-o’-Wisp' },
  winter_wolf: { ru: 'Зимний волк', en: 'Winter Wolf' },
  wolf: { ru: 'Волк', en: 'Wolf' },
  worg: { ru: 'Ворг', en: 'Worg' },
  wraith: { ru: 'Тень смерти', en: 'Wraith' },
  wyvern: { ru: 'Виверна', en: 'Wyvern' },
  xorn: { ru: 'Ксорн', en: 'Xorn' },
  young_black_dragon: { ru: 'Молодой чёрный дракон', en: 'Young Black Dragon' },
  young_blue_dragon: { ru: 'Молодой синий дракон', en: 'Young Blue Dragon' },
  young_brass_dragon: { ru: 'Молодой латунный дракон', en: 'Young Brass Dragon' },
  young_bronze_dragon: { ru: 'Молодой бронзовый дракон', en: 'Young Bronze Dragon' },
  young_copper_dragon: { ru: 'Молодой медный дракон', en: 'Young Copper Dragon' },
  young_gold_dragon: { ru: 'Молодой золотой дракон', en: 'Young Gold Dragon' },
  young_green_dragon: { ru: 'Молодой зелёный дракон', en: 'Young Green Dragon' },
  young_red_dragon: { ru: 'Молодой красный дракон', en: 'Young Red Dragon' },
  young_silver_dragon: { ru: 'Молодой серебряный дракон', en: 'Young Silver Dragon' },
  young_white_dragon: { ru: 'Молодой белый дракон', en: 'Young White Dragon' },
  zombie: { ru: 'Зомби', en: 'Zombie' },

  // ── Все остальные предметы: образцы редактора и выдача мастером ──
  // Раньше переводились только предметы стартовых наборов и инструменты.
  // Остальные 369 выходят на экран тоже — в списке «выдать предмет»
  // у мастера и в списке «взять за основу» редактора записей.

  // Снаряжение вне стартовых наборов
  // Названия — с next.dnd.su/equipment/ (разрешение: docs/dnd-su-permission.md):
  // совпадение по английскому названию, сверено списком.
  acid: { ru: 'Кислота', en: 'Acid' },
  alchemists_fire: { ru: 'Алхимический огонь', en: 'Alchemist’s Fire' },
  ammunition: { ru: 'Боеприпасы', en: 'Ammunition' },
  antitoxin: { ru: 'Противоядие', en: 'Antitoxin' },
  backpack: { ru: 'Рюкзак', en: 'Backpack' },
  ball_bearings: { ru: 'Металлические шарики', en: 'Ball Bearings' },
  barrel: { ru: 'Бочка', en: 'Barrel' },
  basket: { ru: 'Корзина', en: 'Basket' },
  battleaxe: { ru: 'Боевой топор', en: 'Battleaxe' },
  bedroll: { ru: 'Спальник', en: 'Bedroll' },
  bell: { ru: 'Колокольчик', en: 'Bell' },
  blanket: { ru: 'Одеяло', en: 'Blanket' },
  block_and_tackle: { ru: 'Блок и лебёдка', en: 'Block and Tackle' },
  blowgun: { ru: 'Духовая трубка', en: 'Blowgun' },
  bottle_glass: { ru: 'Стеклянная бутылка', en: 'Bottle, Glass' },
  breastplate: { ru: 'Кираса', en: 'Breastplate' },
  bucket: { ru: 'Ведро', en: 'Bucket' },
  caltrops: { ru: 'Калтропы', en: 'Caltrops' },
  candle: { ru: 'Свеча', en: 'Candle' },
  case_crossbow_bolt: { ru: 'Футляр для арбалетных болтов', en: 'Case, Crossbow Bolt' },
  case_map_or_scroll: { ru: 'Тубус для карт и свитков', en: 'Case, Map or Scroll' },
  chain: { ru: 'Цепь', en: 'Chain' },
  chest: { ru: 'Сундук', en: 'Chest' },
  climbers_kit: { ru: 'Комплект для лазания', en: 'Climber’s Kit' },
  clothes_fine: { ru: 'Отличная одежда', en: 'Clothes, Fine' },
  club: { ru: 'Дубинка', en: 'Club' },
  component_pouch: { ru: 'Мешочек с компонентами', en: 'Component Pouch' },
  costume: { ru: 'Костюм', en: 'Costume' },
  dart: { ru: 'Дротик', en: 'Dart' },
  diplomats_pack: { ru: 'Набор дипломата', en: 'Diplomat’s Pack' },
  flask: { ru: 'Фляга', en: 'Flask' },
  glaive: { ru: 'Глефа', en: 'Glaive' },
  grappling_hook: { ru: 'Крюк-кошка', en: 'Grappling Hook' },
  greatclub: { ru: 'Палица', en: 'Greatclub' },
  halberd: { ru: 'Алебарда', en: 'Halberd' },
  half_plate_armor: { ru: 'Полулаты', en: 'Half Plate Armor' },
  hand_crossbow: { ru: 'Одноручный арбалет', en: 'Hand Crossbow' },
  heavy_crossbow: { ru: 'Тяжёлый арбалет', en: 'Heavy Crossbow' },
  hide_armor: { ru: 'Шкурный доспех', en: 'Hide Armor' },
  holy_water: { ru: 'Святая вода', en: 'Holy Water' },
  hunting_trap: { ru: 'Охотничий капкан', en: 'Hunting Trap' },
  ink: { ru: 'Чернила', en: 'Ink' },
  ink_pen: { ru: 'Писчее перо', en: 'Ink Pen' },
  jug: { ru: 'Кувшин', en: 'Jug' },
  ladder: { ru: 'Лестница', en: 'Ladder' },
  lamp: { ru: 'Лампа', en: 'Lamp' },
  lance: { ru: 'Кавалерийское копьё', en: 'Lance' },
  lantern_bullseye: { ru: 'Направленный фонарь', en: 'Lantern, Bullseye' },
  lantern_hooded: { ru: 'Закрытый фонарь', en: 'Lantern, Hooded' },
  light_crossbow: { ru: 'Лёгкий арбалет', en: 'Light Crossbow' },
  light_hammer: { ru: 'Лёгкий молот', en: 'Light Hammer' },
  lock: { ru: 'Замок', en: 'Lock' },
  magnifying_glass: { ru: 'Увеличительное стекло', en: 'Magnifying Glass' },
  manacles: { ru: 'Кандалы', en: 'Manacles' },
  map: { ru: 'Карта', en: 'Map' },
  maul: { ru: 'Молот', en: 'Maul' },
  mirror: { ru: 'Зеркало', en: 'Mirror' },
  morningstar: { ru: 'Моргенштерн', en: 'Morningstar' },
  musket: { ru: 'Мушкет', en: 'Musket' },
  net: { ru: 'Сеть', en: 'Net' },
  oil: { ru: 'Масло', en: 'Oil' },
  padded_armor: { ru: 'Стёганый доспех', en: 'Padded Armor' },
  paper: { ru: 'Бумага', en: 'Paper' },
  perfume: { ru: 'Духи', en: 'Perfume' },
  pike: { ru: 'Пика', en: 'Pike' },
  pistol: { ru: 'Пистоль', en: 'Pistol' },
  plate_armor: { ru: 'Латный доспех', en: 'Plate Armor' },
  poison_basic: { ru: 'Простой яд', en: 'Poison, Basic' },
  pole: { ru: 'Шест', en: 'Pole' },
  pot_iron: { ru: 'Железный горшок', en: 'Pot, Iron' },
  potion_of_healing: { ru: 'Зелье лечения', en: 'Potion of Healing' },
  ram_portable: { ru: 'Портативный таран', en: 'Ram, Portable' },
  rapier: { ru: 'Рапира', en: 'Rapier' },
  rations: { ru: 'Рационы', en: 'Rations' },
  ring_mail: { ru: 'Колечный доспех', en: 'Ring Mail' },
  rope: { ru: 'Верёвка', en: 'Rope' },
  sack: { ru: 'Мешок', en: 'Sack' },
  scale_mail: { ru: 'Чешуйчатый доспех', en: 'Scale Mail' },
  shovel: { ru: 'Лопата', en: 'Shovel' },
  signal_whistle: { ru: 'Сигнальный свисток', en: 'Signal Whistle' },
  sling: { ru: 'Праща', en: 'Sling' },
  spikes_iron: { ru: 'Железные шипы', en: 'Spikes, Iron' },
  splint_armor: { ru: 'Пластинчатый доспех', en: 'Splint Armor' },
  spyglass: { ru: 'Подзорная труба', en: 'Spyglass' },
  string: { ru: 'Леска', en: 'String' },
  tent: { ru: 'Палатка', en: 'Tent' },
  tinderbox: { ru: 'Трутница', en: 'Tinderbox' },
  torch: { ru: 'Факел', en: 'Torch' },
  trident: { ru: 'Трезубец', en: 'Trident' },
  vial: { ru: 'Флакон', en: 'Vial' },
  war_pick: { ru: 'Клевец', en: 'War Pick' },
  warhammer: { ru: 'Боевой молот', en: 'Warhammer' },
  whip: { ru: 'Кнут', en: 'Whip' },

  // Снаряжение вне стартовых наборов: составные имена
  // В SRD «Основа, уточнение» одной строкой; на next.dnd.su основа
  // и уточнение — две позиции. Слова взяты оттуда, склейка наша.
  // Фокусировки названы так же, как в стартовых наборах (`slots.ts`):
  // там «шар» и «ветка омелы», на next.dnd.su — «Сфера» и «Веточка
  // омелы». Одно понятие — одно название (D59); какое оставить —
  // решение на будущее, а не правка мимоходом.
  ammunition_bolts: { ru: 'Боеприпасы: болты', en: 'Ammunition, Bolts' },
  ammunition_bullets_firearm: {
    ru: 'Боеприпасы: пули',
    en: 'Ammunition, Bullets, Firearm',
  },
  ammunition_bullets_sling: {
    ru: 'Боеприпасы: снаряды для пращи',
    en: 'Ammunition, Bullets, Sling',
  },
  ammunition_needles: { ru: 'Боеприпасы: иглы', en: 'Ammunition, Needles' },
  arcane_focus_crystal: {
    ru: 'Магическая фокусировка (кристалл)',
    en: 'Arcane Focus, Crystal',
  },
  arcane_focus_orb: { ru: 'Магическая фокусировка (шар)', en: 'Arcane Focus, Orb' },
  arcane_focus_rod: { ru: 'Магическая фокусировка (жезл)', en: 'Arcane Focus, Rod' },
  arcane_focus_staff_also_a_quarterstaff: {
    ru: 'Магическая фокусировка (посох, он же боевой посох)',
    en: 'Arcane Focus, Staff (also a Quarterstaff)',
  },
  arcane_focus_wand: {
    ru: 'Магическая фокусировка (волшебная палочка)',
    en: 'Arcane Focus, Wand',
  },
  druidic_focus_sprig_of_mistletoe: {
    ru: 'Друидическая фокусировка (ветка омелы)',
    en: 'Druidic Focus, Sprig of mistletoe',
  },
  druidic_focus_wooden_staff_also_a_quarterstaff: {
    ru: 'Друидическая фокусировка (деревянный посох, он же боевой посох)',
    en: 'Druidic Focus, Wooden staff (also a Quarterstaff)',
  },
  druidic_focus_yew_wand: {
    ru: 'Друидическая фокусировка (тисовая палочка)',
    en: 'Druidic Focus, Yew wand',
  },
  holy_symbol_amulet_worn_or_held: {
    ru: 'Священный символ (амулет: носят или держат в руке)',
    en: 'Holy Symbol, Amulet (worn or held)',
  },
  holy_symbol_emblem_borne_on_fabric_or_a_shield: {
    ru: 'Священный символ (эмблема на ткани или щите)',
    en: 'Holy Symbol, Emblem (borne on fabric or a Shield)',
  },
  holy_symbol_reliquary_held: {
    ru: 'Священный символ (реликварий: держат в руке)',
    en: 'Holy Symbol, Reliquary (held)',
  },

  // Ездовые животные: те же имена, что у зверей справочника
  // Предмет «Mastiff» и зверь «Mastiff» — одно животное: купленное
  // и встреченное. Имя одно (D59).
  camel: { ru: 'Верблюд', en: 'Camel' },
  elephant: { ru: 'Слон', en: 'Elephant' },
  horse_draft: { ru: 'Тягловая лошадь', en: 'Horse, Draft' },
  horse_riding: { ru: 'Верховая лошадь', en: 'Horse, Riding' },
  mastiff: { ru: 'Мастиф', en: 'Mastiff' },
  mule: { ru: 'Мул', en: 'Mule' },
  pony: { ru: 'Пони', en: 'Pony' },
  warhorse: { ru: 'Боевой конь', en: 'Warhorse' },

  // Транспорт, сёдла и содержание
  // На next.dnd.su этих позиций нет вовсе: ни транспорта, ни сёдел,
  // ни корма. Названия наши, общеупотребительные; сверить при случае.
  carriage: { ru: 'Карета', en: 'Carriage' },
  cart: { ru: 'Телега', en: 'Cart' },
  chariot: { ru: 'Колесница', en: 'Chariot' },
  feed_per_day: { ru: 'Корм (на день)', en: 'Feed per day' },
  saddle_exotic: { ru: 'Седло: экзотическое', en: 'Saddle, Exotic' },
  saddle_military: { ru: 'Седло: военное', en: 'Saddle, Military' },
  saddle_riding: { ru: 'Седло: верховое', en: 'Saddle, Riding' },
  sled: { ru: 'Сани', en: 'Sled' },
  stabling_per_day: { ru: 'Стойло (на день)', en: 'Stabling per day' },
  wagon: { ru: 'Фургон', en: 'Wagon' },

  // Магические предметы
  // Названия — с next.dnd.su/items/, совпадение по английскому
  // названию, сверено списком.
  adamantine_armor: { ru: 'Адамантиновый доспех', en: 'Adamantine Armor' },
  ammunition_1_2_or_3: { ru: 'Боеприпас +1, +2, +3', en: 'Ammunition, +1, +2, or +3' },
  ammunition_of_slaying: { ru: 'Боеприпас убийства', en: 'Ammunition of Slaying' },
  amulet_of_health: { ru: 'Амулет здоровья', en: 'Amulet of Health' },
  amulet_of_proof_against_detection_and_location: {
    ru: 'Амулет защиты от обнаружения и поиска',
    en: 'Amulet of Proof against Detection and Location',
  },
  amulet_of_the_planes: { ru: 'Амулет планов', en: 'Amulet of the Planes' },
  animated_shield: { ru: 'Живой щит', en: 'Animated Shield' },
  armor_1_2_or_3: { ru: 'Доспех +1, +2, +3', en: 'Armor, +1, +2, or +3' },
  armor_of_invulnerability: {
    ru: 'Доспех неуязвимости',
    en: 'Armor of Invulnerability',
  },
  armor_of_resistance: { ru: 'Доспех сопротивления', en: 'Armor of Resistance' },
  armor_of_vulnerability: { ru: 'Доспех уязвимости', en: 'Armor of Vulnerability' },
  arrow_catching_shield: { ru: 'Ловящий стрелы щит', en: 'Arrow-Catching Shield' },
  bag_of_beans: { ru: 'Сумка с бобами', en: 'Bag of Beans' },
  bag_of_devouring: { ru: 'Сумка пожирания', en: 'Bag of Devouring' },
  bag_of_holding: { ru: 'Сумка хранения', en: 'Bag of Holding' },
  bag_of_tricks: { ru: 'Сумка фокусов', en: 'Bag of Tricks' },
  bead_of_force: { ru: 'Бусина силы', en: 'Bead of Force' },
  belt_of_dwarvenkind: { ru: 'Пояс дварфов', en: 'Belt of Dwarvenkind' },
  belt_of_giant_strength: { ru: 'Пояс силы великана', en: 'Belt of Giant Strength' },
  berserker_axe: { ru: 'Топор берсерка', en: 'Berserker Axe' },
  boots_of_elvenkind: { ru: 'Эльфийские сапоги', en: 'Boots of Elvenkind' },
  boots_of_levitation: { ru: 'Сапоги левитации', en: 'Boots of Levitation' },
  boots_of_speed: { ru: 'Сапоги скорости', en: 'Boots of Speed' },
  boots_of_striding_and_springing: {
    ru: 'Сапоги ходьбы и прыжков',
    en: 'Boots of Striding and Springing',
  },
  boots_of_the_winterlands: { ru: 'Заполярные сапоги', en: 'Boots of the Winterlands' },
  bowl_of_commanding_water_elementals: {
    ru: 'Чаша командования водяными элементалями',
    en: 'Bowl of Commanding Water Elementals',
  },
  bracers_of_archery: { ru: 'Наручи стрельбы из лука', en: 'Bracers of Archery' },
  bracers_of_defense: { ru: 'Наручи защиты', en: 'Bracers of Defense' },
  brazier_of_commanding_fire_elementals: {
    ru: 'Жаровня командования огненными элементалями',
    en: 'Brazier of Commanding Fire Elementals',
  },
  brooch_of_shielding: { ru: 'Брошь защиты', en: 'Brooch of Shielding' },
  broom_of_flying: { ru: 'Помело полёта', en: 'Broom of Flying' },
  candle_of_invocation: { ru: 'Свеча мольбы', en: 'Candle of Invocation' },
  cape_of_the_mountebank: { ru: 'Плащ шарлатана', en: 'Cape of the Mountebank' },
  carpet_of_flying: { ru: 'Ковёр-самолёт', en: 'Carpet of Flying' },
  censer_of_controlling_air_elementals: {
    ru: 'Кадило контролирования воздушных элементалей',
    en: 'Censer of Controlling Air Elementals',
  },
  chime_of_opening: { ru: 'Колокольчик открывания', en: 'Chime of Opening' },
  circlet_of_blasting: { ru: 'Обруч сжигания', en: 'Circlet of Blasting' },
  cloak_of_arachnida: { ru: 'Плащ паука', en: 'Cloak of Arachnida' },
  cloak_of_displacement: { ru: 'Плащ ускользания', en: 'Cloak of Displacement' },
  cloak_of_elvenkind: { ru: 'Эльфийский плащ', en: 'Cloak of Elvenkind' },
  cloak_of_protection: { ru: 'Плащ защиты', en: 'Cloak of Protection' },
  cloak_of_the_bat: { ru: 'Плащ летучей мыши', en: 'Cloak of the Bat' },
  cloak_of_the_manta_ray: { ru: 'Плащ ската', en: 'Cloak of the Manta Ray' },
  crystal_ball: { ru: 'Хрустальный шар', en: 'Crystal Ball' },
  crystal_ball_of_mind_reading: {
    ru: 'Хрустальный шар чтения мыслей',
    en: 'Crystal Ball of Mind Reading',
  },
  crystal_ball_of_telepathy: {
    ru: 'Хрустальный шар телепатии',
    en: 'Crystal Ball of Telepathy',
  },
  crystal_ball_of_true_seeing: {
    ru: 'Хрустальный шар истинного зрения',
    en: 'Crystal Ball of True Seeing',
  },
  cube_of_force: { ru: 'Куб силы', en: 'Cube of Force' },
  cubic_gate: { ru: 'Куб врат', en: 'Cubic Gate' },
  dagger_of_venom: { ru: 'Кинжал яда', en: 'Dagger of Venom' },
  dancing_sword: { ru: 'Танцующий меч', en: 'Dancing Sword' },
  decanter_of_endless_water: {
    ru: 'Графин бесконечной воды',
    en: 'Decanter of Endless Water',
  },
  deck_of_illusions: { ru: 'Колода иллюзий', en: 'Deck of Illusions' },
  defender: { ru: 'Защитник', en: 'Defender' },
  demon_armor: { ru: 'Демонический доспех', en: 'Demon Armor' },
  dimensional_shackles: { ru: 'Оковы измерений', en: 'Dimensional Shackles' },
  dragon_scale_mail: { ru: 'Доспех из драконьей чешуи', en: 'Dragon Scale Mail' },
  dragon_slayer: { ru: 'Убийца драконов', en: 'Dragon Slayer' },
  dust_of_disappearance: { ru: 'Порошок исчезновения', en: 'Dust of Disappearance' },
  dust_of_dryness: { ru: 'Порошок сухости', en: 'Dust of Dryness' },
  dust_of_sneezing_and_choking: {
    ru: 'Порошок чихания и удушья',
    en: 'Dust of Sneezing and Choking',
  },
  dwarven_plate: { ru: 'Латы дварфов', en: 'Dwarven Plate' },
  dwarven_thrower: { ru: 'Дварфийский метатель', en: 'Dwarven Thrower' },
  efreeti_bottle: { ru: 'Бутылка с ифритом', en: 'Efreeti Bottle' },
  elemental_gem: { ru: 'Камень элементаля', en: 'Elemental Gem' },
  elven_chain: { ru: 'Эльфийская кольчуга', en: 'Elven Chain' },
  eversmoking_bottle: { ru: 'Вечнодымящаяся бутылка', en: 'Eversmoking Bottle' },
  eyes_of_charming: { ru: 'Очи обольщения', en: 'Eyes of Charming' },
  eyes_of_minute_seeing: { ru: 'Очки детального зрения', en: 'Eyes of Minute Seeing' },
  eyes_of_the_eagle: { ru: 'Очки орлиного зрения', en: 'Eyes of the Eagle' },
  figurine_of_wondrous_power: {
    ru: 'Статуэтка чудесной силы',
    en: 'Figurine of Wondrous Power',
  },
  flame_tongue: { ru: 'Язык пламени', en: 'Flame Tongue' },
  folding_boat: { ru: 'Складная лодка', en: 'Folding Boat' },
  frost_brand: { ru: 'Морозный клинок', en: 'Frost Brand' },
  gauntlets_of_ogre_power: { ru: 'Рукавицы силы огра', en: 'Gauntlets of Ogre Power' },
  gem_of_brightness: { ru: 'Камень сияния', en: 'Gem of Brightness' },
  gem_of_seeing: { ru: 'Камень зрения', en: 'Gem of Seeing' },
  giant_slayer: { ru: 'Убийца великанов', en: 'Giant Slayer' },
  glamoured_studded_leather: {
    ru: 'Очарованный проклёпанный кожаный доспех',
    en: 'Glamoured Studded Leather',
  },
  gloves_of_missile_snaring: {
    ru: 'Перчатки ловли снарядов',
    en: 'Gloves of Missile Snaring',
  },
  gloves_of_swimming_and_climbing: {
    ru: 'Перчатки плавания и лазания',
    en: 'Gloves of Swimming and Climbing',
  },
  goggles_of_night: { ru: 'Ночные очки', en: 'Goggles of Night' },
  hammer_of_thunderbolts: { ru: 'Молот грома', en: 'Hammer of Thunderbolts' },
  hat_of_disguise: { ru: 'Шапка маскировки', en: 'Hat of Disguise' },
  headband_of_intellect: { ru: 'Повязка интеллекта', en: 'Headband of Intellect' },
  helm_of_brilliance: { ru: 'Шлем блеска', en: 'Helm of Brilliance' },
  helm_of_comprehending_languages: {
    ru: 'Шлем понимания языков',
    en: 'Helm of Comprehending Languages',
  },
  helm_of_telepathy: { ru: 'Шлем телепатии', en: 'Helm of Telepathy' },
  helm_of_teleportation: { ru: 'Шлем телепортации', en: 'Helm of Teleportation' },
  holy_avenger: { ru: 'Святой мститель', en: 'Holy Avenger' },
  horn_of_blasting: { ru: 'Рог взрыва', en: 'Horn of Blasting' },
  horn_of_valhalla: { ru: 'Рог Вальхаллы', en: 'Horn of Valhalla' },
  horseshoes_of_a_zephyr: { ru: 'Подковы ветра', en: 'Horseshoes of a Zephyr' },
  horseshoes_of_speed: { ru: 'Подковы скорости', en: 'Horseshoes of Speed' },
  immovable_rod: { ru: 'Неподвижный жезл', en: 'Immovable Rod' },
  ioun_stone: { ru: 'Камень Айун', en: 'Ioun Stone' },
  iron_flask: { ru: 'Железная фляга', en: 'Iron Flask' },
  javelin_of_lightning: { ru: 'Метательное копьё молнии', en: 'Javelin of Lightning' },
  lantern_of_revealing: { ru: 'Фонарь обнаружения', en: 'Lantern of Revealing' },
  luck_blade: { ru: 'Клинок удачи', en: 'Luck Blade' },
  mace_of_disruption: { ru: 'Булава распада', en: 'Mace of Disruption' },
  mace_of_smiting: { ru: 'Булава кары', en: 'Mace of Smiting' },
  mace_of_terror: { ru: 'Булава ужаса', en: 'Mace of Terror' },
  mantle_of_spell_resistance: {
    ru: 'Мантия сопротивления заклинаниям',
    en: 'Mantle of Spell Resistance',
  },
  manual_of_bodily_health: {
    ru: 'Справочник телесного здоровья',
    en: 'Manual of Bodily Health',
  },
  manual_of_gainful_exercise: {
    ru: 'Справочник полезных упражнений',
    en: 'Manual of Gainful Exercise',
  },
  manual_of_golems: { ru: 'Справочник по големам', en: 'Manual of Golems' },
  manual_of_quickness_of_action: {
    ru: 'Справочник быстроты действий',
    en: 'Manual of Quickness of Action',
  },
  medallion_of_thoughts: { ru: 'Медальон мыслей', en: 'Medallion of Thoughts' },
  mirror_of_life_trapping: {
    ru: 'Зеркало похищения жизни',
    en: 'Mirror of Life Trapping',
  },
  mithral_armor: { ru: 'Мифрильный доспех', en: 'Mithral Armor' },
  necklace_of_adaptation: { ru: 'Ожерелье адаптации', en: 'Necklace of Adaptation' },
  necklace_of_fireballs: { ru: 'Ожерелье огненных шаров', en: 'Necklace of Fireballs' },
  necklace_of_prayer_beads: {
    ru: 'Ожерелье молитвенных чёток',
    en: 'Necklace of Prayer Beads',
  },
  nine_lives_stealer: { ru: 'Вор девяти жизней', en: 'Nine Lives Stealer' },
  oathbow: { ru: 'Лук клятвы', en: 'Oathbow' },
  oil_of_etherealness: { ru: 'Масло эфирности', en: 'Oil of Etherealness' },
  oil_of_sharpness: { ru: 'Масло остроты', en: 'Oil of Sharpness' },
  oil_of_slipperiness: { ru: 'Масло ускользания', en: 'Oil of Slipperiness' },
  pearl_of_power: { ru: 'Жемчужина силы', en: 'Pearl of Power' },
  periapt_of_health: { ru: 'Медальон здоровья', en: 'Periapt of Health' },
  periapt_of_proof_against_poison: {
    ru: 'Медальон защиты от яда',
    en: 'Periapt of Proof against Poison',
  },
  periapt_of_wound_closure: {
    ru: 'Медальон затягивающихся ран',
    en: 'Periapt of Wound Closure',
  },
  philter_of_love: { ru: 'Любовное зелье', en: 'Philter of Love' },
  pipes_of_haunting: { ru: 'Свирель ужаса', en: 'Pipes of Haunting' },
  pipes_of_the_sewers: { ru: 'Свирель канализации', en: 'Pipes of the Sewers' },
  plate_armor_of_etherealness: {
    ru: 'Латный доспех эфирности',
    en: 'Plate Armor of Etherealness',
  },
  portable_hole: { ru: 'Переносная дыра', en: 'Portable Hole' },
  potion_of_animal_friendship: {
    ru: 'Зелье дружбы с животными',
    en: 'Potion of Animal Friendship',
  },
  potion_of_clairvoyance: { ru: 'Зелье ясновидения', en: 'Potion of Clairvoyance' },
  potion_of_climbing: { ru: 'Зелье лазания', en: 'Potion of Climbing' },
  potion_of_diminution: { ru: 'Зелье уменьшения', en: 'Potion of Diminution' },
  potion_of_flying: { ru: 'Зелье полёта', en: 'Potion of Flying' },
  potion_of_gaseous_form: {
    ru: 'Зелье газообразной формы',
    en: 'Potion of Gaseous Form',
  },
  potion_of_giant_strength: {
    ru: 'Зелье силы великана',
    en: 'Potion of Giant Strength',
  },
  potion_of_growth: { ru: 'Зелье увеличения', en: 'Potion of Growth' },
  potion_of_heroism: { ru: 'Зелье героизма', en: 'Potion of Heroism' },
  potion_of_invisibility: { ru: 'Зелье невидимости', en: 'Potion of Invisibility' },
  potion_of_mind_reading: { ru: 'Зелье чтения мыслей', en: 'Potion of Mind Reading' },
  potion_of_poison: { ru: 'Зелье яда', en: 'Potion of Poison' },
  potion_of_resistance: { ru: 'Зелье сопротивления', en: 'Potion of Resistance' },
  potion_of_speed: { ru: 'Зелье скорости', en: 'Potion of Speed' },
  potion_of_water_breathing: {
    ru: 'Зелье подводного дыхания',
    en: 'Potion of Water Breathing',
  },
  ring_of_animal_influence: {
    ru: 'Кольцо влияния на животных',
    en: 'Ring of Animal Influence',
  },
  ring_of_djinni_summoning: {
    ru: 'Кольцо призыва джинна',
    en: 'Ring of Djinni Summoning',
  },
  ring_of_elemental_command: {
    ru: 'Кольцо командования элементалями',
    en: 'Ring of Elemental Command',
  },
  ring_of_evasion: { ru: 'Кольцо уклонения', en: 'Ring of Evasion' },
  ring_of_feather_falling: {
    ru: 'Кольцо падения пёрышком',
    en: 'Ring of Feather Falling',
  },
  ring_of_free_action: { ru: 'Кольцо свободных действий', en: 'Ring of Free Action' },
  ring_of_invisibility: { ru: 'Кольцо невидимости', en: 'Ring of Invisibility' },
  ring_of_jumping: { ru: 'Кольцо прыжков', en: 'Ring of Jumping' },
  ring_of_mind_shielding: { ru: 'Кольцо защиты разума', en: 'Ring of Mind Shielding' },
  ring_of_protection: { ru: 'Кольцо защиты', en: 'Ring of Protection' },
  ring_of_regeneration: { ru: 'Кольцо регенерации', en: 'Ring of Regeneration' },
  ring_of_resistance: { ru: 'Кольцо сопротивления', en: 'Ring of Resistance' },
  ring_of_shooting_stars: { ru: 'Кольцо падающих звёзд', en: 'Ring of Shooting Stars' },
  ring_of_spell_storing: {
    ru: 'Кольцо хранения заклинаний',
    en: 'Ring of Spell Storing',
  },
  ring_of_spell_turning: {
    ru: 'Кольцо отражения заклинаний',
    en: 'Ring of Spell Turning',
  },
  ring_of_swimming: { ru: 'Кольцо плавания', en: 'Ring of Swimming' },
  ring_of_telekinesis: { ru: 'Кольцо телекинеза', en: 'Ring of Telekinesis' },
  ring_of_the_ram: { ru: 'Кольцо барана', en: 'Ring of the Ram' },
  ring_of_three_wishes: { ru: 'Кольцо трёх желаний', en: 'Ring of Three Wishes' },
  ring_of_warmth: { ru: 'Кольцо тепла', en: 'Ring of Warmth' },
  ring_of_water_walking: { ru: 'Кольцо хождения по воде', en: 'Ring of Water Walking' },
  ring_of_x_ray_vision: {
    ru: 'Кольцо проникающего зрения',
    en: 'Ring of X-ray Vision',
  },
  robe_of_eyes: { ru: 'Мантия глаз', en: 'Robe of Eyes' },
  robe_of_scintillating_colors: {
    ru: 'Мантия сияющих цветов',
    en: 'Robe of Scintillating Colors',
  },
  robe_of_stars: { ru: 'Мантия звёзд', en: 'Robe of Stars' },
  robe_of_the_archmagi: { ru: 'Мантия архимага', en: 'Robe of the Archmagi' },
  robe_of_useful_items: { ru: 'Мантия полезных предметов', en: 'Robe of Useful Items' },
  rod_of_absorption: { ru: 'Жезл поглощения', en: 'Rod of Absorption' },
  rod_of_alertness: { ru: 'Жезл бдительности', en: 'Rod of Alertness' },
  rod_of_lordly_might: { ru: 'Жезл величественной мощи', en: 'Rod of Lordly Might' },
  rod_of_rulership: { ru: 'Жезл правления', en: 'Rod of Rulership' },
  rod_of_security: { ru: 'Жезл безопасности', en: 'Rod of Security' },
  rope_of_climbing: { ru: 'Верёвка лазания', en: 'Rope of Climbing' },
  rope_of_entanglement: { ru: 'Верёвка опутывания', en: 'Rope of Entanglement' },
  scarab_of_protection: { ru: 'Скарабей защиты', en: 'Scarab of Protection' },
  scimitar_of_speed: { ru: 'Скимитар скорости', en: 'Scimitar of Speed' },
  shield_1_2_or_3: { ru: 'Щит +1, +2, +3', en: 'Shield, +1, +2, or +3' },
  shield_of_missile_attraction: {
    ru: 'Щит притягивания снарядов',
    en: 'Shield of Missile Attraction',
  },
  slippers_of_spider_climbing: { ru: 'Туфли паука', en: 'Slippers of Spider Climbing' },
  sovereign_glue: { ru: 'Превосходный клей', en: 'Sovereign Glue' },
  spell_scroll: { ru: 'Свиток заклинания', en: 'Spell Scroll' },
  spellguard_shield: { ru: 'Щит ограждения от заклятий', en: 'Spellguard Shield' },
  sphere_of_annihilation: { ru: 'Сфера аннигиляции', en: 'Sphere of Annihilation' },
  staff_of_charming: { ru: 'Посох очарования', en: 'Staff of Charming' },
  staff_of_fire: { ru: 'Посох огня', en: 'Staff of Fire' },
  staff_of_frost: { ru: 'Посох мороза', en: 'Staff of Frost' },
  staff_of_healing: { ru: 'Посох лечения', en: 'Staff of Healing' },
  staff_of_power: { ru: 'Посох силы', en: 'Staff of Power' },
  staff_of_striking: { ru: 'Посох ударов', en: 'Staff of Striking' },
  staff_of_swarming_insects: {
    ru: 'Посох роя насекомых',
    en: 'Staff of Swarming Insects',
  },
  staff_of_the_magi: { ru: 'Посох магов', en: 'Staff of the Magi' },
  staff_of_the_python: { ru: 'Посох питона', en: 'Staff of the Python' },
  staff_of_the_woodlands: { ru: 'Посох леса', en: 'Staff of the Woodlands' },
  staff_of_thunder_and_lightning: {
    ru: 'Посох грома и молнии',
    en: 'Staff of Thunder and Lightning',
  },
  staff_of_withering: { ru: 'Посох иссушения', en: 'Staff of Withering' },
  stone_of_controlling_earth_elementals: {
    ru: 'Камень контролирования земляных элементалей',
    en: 'Stone of Controlling Earth Elementals',
  },
  stone_of_good_luck_luckstone: {
    ru: 'Камень удачи',
    en: 'Stone of Good Luck (Luckstone)',
  },
  sun_blade: { ru: 'Солнечный клинок', en: 'Sun Blade' },
  sword_of_life_stealing: { ru: 'Меч кражи жизни', en: 'Sword of Life Stealing' },
  sword_of_sharpness: { ru: 'Меч остроты', en: 'Sword of Sharpness' },
  sword_of_wounding: { ru: 'Меч ранения', en: 'Sword of Wounding' },
  talisman_of_pure_good: { ru: 'Талисман чистого добра', en: 'Talisman of Pure Good' },
  talisman_of_the_sphere: { ru: 'Талисман сферы', en: 'Talisman of the Sphere' },
  talisman_of_ultimate_evil: {
    ru: 'Талисман абсолютного зла',
    en: 'Talisman of Ultimate Evil',
  },
  tome_of_clear_thought: { ru: 'Фолиант чистых мыслей', en: 'Tome of Clear Thought' },
  tome_of_leadership_and_influence: {
    ru: 'Фолиант лидерства и влияния',
    en: 'Tome of Leadership and Influence',
  },
  tome_of_understanding: { ru: 'Фолиант понимания', en: 'Tome of Understanding' },
  trident_of_fish_command: {
    ru: 'Трезубец командования рыбами',
    en: 'Trident of Fish Command',
  },
  universal_solvent: { ru: 'Универсальный растворитель', en: 'Universal Solvent' },
  vicious_weapon: { ru: 'Жестокое оружие', en: 'Vicious Weapon' },
  vorpal_sword: { ru: 'Вострый меч', en: 'Vorpal Sword' },
  wand_of_binding: { ru: 'Волшебная палочка сковывания', en: 'Wand of Binding' },
  wand_of_enemy_detection: {
    ru: 'Волшебная палочка обнаружения врагов',
    en: 'Wand of Enemy Detection',
  },
  wand_of_fear: { ru: 'Волшебная палочка страха', en: 'Wand of Fear' },
  wand_of_fireballs: {
    ru: 'Волшебная палочка огненных шаров',
    en: 'Wand of Fireballs',
  },
  wand_of_lightning_bolts: {
    ru: 'Волшебная палочка молний',
    en: 'Wand of Lightning Bolts',
  },
  wand_of_magic_detection: {
    ru: 'Волшебная палочка обнаружения магии',
    en: 'Wand of Magic Detection',
  },
  wand_of_magic_missiles: {
    ru: 'Волшебная палочка стрел',
    en: 'Wand of Magic Missiles',
  },
  wand_of_paralysis: { ru: 'Волшебная палочка паралича', en: 'Wand of Paralysis' },
  wand_of_polymorph: { ru: 'Волшебная палочка превращения', en: 'Wand of Polymorph' },
  wand_of_secrets: { ru: 'Волшебная палочка секретов', en: 'Wand of Secrets' },
  wand_of_the_war_mage_1_2_or_3: {
    ru: 'Волшебная палочка боевого мага +1, +2, +3',
    en: 'Wand of the War Mage, +1, +2, or +3',
  },
  wand_of_web: { ru: 'Волшебная палочка паутины', en: 'Wand of Web' },
  wand_of_wonder: { ru: 'Волшебная палочка чудес', en: 'Wand of Wonder' },
  weapon_1_2_or_3: { ru: 'Оружие +1, +2, +3', en: 'Weapon, +1, +2, or +3' },
  weapon_of_warning: { ru: 'Оружие предупреждения', en: 'Weapon of Warning' },
  well_of_many_worlds: { ru: 'Колодец многих миров', en: 'Well of Many Worlds' },
  wind_fan: { ru: 'Веер ветра', en: 'Wind Fan' },
  winged_boots: { ru: 'Крылатые сапоги', en: 'Winged Boots' },
  wings_of_flying: { ru: 'Крылья полёта', en: 'Wings of Flying' },

  // Магические предметы, переименованные в SRD 5.2
  // SRD 5.2 убрал имена собственные: «Heward’s Handy Haversack» стал
  // «Handy Haversack». Перевод — тот же, что на next.dnd.su, без имени
  // («Удобный рюкзак Хеварда» → «Удобный рюкзак»). Где без имени
  // остаётся двусмысленное слово, формулировка наша: «Перо» — уже
  // писчее перо, поэтому «Перо-талисман»; «Колода многих вещей» стала
  // в SRD «Mysterious Deck» — «Таинственная колода».
  apparatus_of_the_crab: { ru: 'Аппарат краба', en: 'Apparatus of the Crab' },
  dragon_orb: { ru: 'Око Дракона', en: 'Dragon Orb' },
  efficient_quiver: { ru: 'Вместительный колчан', en: 'Efficient Quiver' },
  feather_token: { ru: 'Перо-талисман', en: 'Feather Token' },
  handy_haversack: { ru: 'Удобный рюкзак', en: 'Handy Haversack' },
  instant_fortress: { ru: 'Мгновенная крепость', en: 'Instant Fortress' },
  iron_bands: { ru: 'Железные ленты', en: 'Iron Bands' },
  marvelous_pigments: { ru: 'Чудесные краски', en: 'Marvelous Pigments' },
  mysterious_deck: { ru: 'Таинственная колода', en: 'Mysterious Deck' },
  potions_of_healing: { ru: 'Зелья лечения', en: 'Potions of Healing' },
}

/** Ключ записи из идентификатора: `srd:dwarf` → `dwarf`. */
export function termKey(id: string): string {
  return id.startsWith('srd:') ? id.slice('srd:'.length) : id
}

/** Словарь одного языка: то, что уходит в ресурсы i18next. */
export function termsFor(language: 'ru' | 'en'): Record<string, string> {
  return Object.fromEntries(
    Object.entries(TERMS).map(([key, term]) => [key, term[language]]),
  )
}
